import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type ComponentType } from 'react'
import { activeLanguage, translate, useI18n } from '../../../lib/i18n'
import { formatters, intlLocale } from '@webbuilder/i18n'
import { analyticsQueryKey, getBlog, disputeNeedsAction, disputeUnderReview, fetchTeam, getAccountUsage, getSalesAnalytics, getStripeBalance, updateProjectNotes, getProjectNotes, getProjectAnalytics, listBookings, listContacts, listOffers, updateBooking, listOrders, listProjectDomains, listSubmissions, publicSiteUrl, type AccountUsage, type AnalyticsRange, type BlogOverview, type BillingPlan, type CustomDomain, type Booking, type ContactRecord, type FormSubmission, type Offer, type Order, type ProjectAnalytics, type SalesAnalytics, type StripeBalance, openPublishedAsOwner } from '../../../lib/api'
import { NOTES_MAX, NOTE_TEXT_MAX, mergeNotes, notesFit, parseNotes, serializeNotes, type ProjectNoteItem } from '../../../lib/projectNotes'
import { fakeAnalytics } from '../../../lib/fakeAnalytics'
import { KIND_ICON, KIND_KEY, formatMoney, kindOf, mainPrice, orderStatusLabel, payoutArrival, awaitsShipping } from './offerShared'
import { groupBookings, peopleLabel, type BookingEntry } from './bookingShared'
import { GroupBookingDialog } from './BookingsTable'
import { BookingDetail } from './BookingDetail'
import { useRegion } from '../../../lib/region'
import { FeatureUpsell } from '../FeatureUpsell'
import { OrderDetailDialog } from './OrderDetailDialog'
import { SITE_PLAN_LIMITS, usageAlertMessage, usageState, type UsageResource } from '../../../lib/planLimits'
import { getCachedAnalytics, getCachedSalesAnalytics, setCachedAnalytics, setCachedSalesAnalytics } from '../../../lib/analyticsCache'
import { readSnapshot, writeSnapshot } from '../../../lib/overviewSnapshot'
import { markDashboardItemsSeen, readDashboardSeenIds } from '../../../lib/dashboardUnread'
import { dashboardSitePages, type Page } from '../../PageManager/types'
import type { CmsCollection } from '../cmsTypes'
import type { WorkspaceSection } from '../WorkspaceSidebar'
import { TrafficChart } from '../TrafficChart'
import { MetricChart } from '../MetricChart'
import { createPortal } from 'react-dom'
import { CustomizableDashboardGrid, WIDGET_TOOLBAR_SLOT_ID, type DashboardWidgetSpec } from '../CustomizableDashboardGrid'
import { MoneyMetric } from './analyticsShared'
import { MascotColorPicker } from '../MascotColorPicker'
import { MascotGreeting } from '../MascotGreeting'
import { HomeAssistant } from '../../Assistant/HomeAssistant'
import { useAssistant } from '../../../lib/dashboardAssistant'
import { TeamInviteBanner } from '../../TeamInviteBanner'
import { TransferBanner } from '../../TransferBanner'
import { ProjectHandover } from '../../ProjectHandover'
import { topNotice, usePendingNotices } from '../../../lib/pendingNotices'
import { can, type ProjectAccessRole, type ProjectRole } from '@webbuilder/shared-types'
import type { MascotFact } from '../../../lib/mascotLines'
import { IconAddressBook, IconAlertCircle, IconArticle, IconCalendarEvent, IconArrowDownRight, IconArrowUpRight, IconBrowserPlus, IconBuildingBank, IconCash, IconChartBar, IconCheck, IconChevronLeft, IconChevronRight, IconClick, IconClock, IconClipboardList, IconCloudOff, IconCoins, IconCopy, IconDatabase, IconDots, IconExternalLink, IconEye, IconFileText, IconFiles, IconGauge, IconHome, IconLayout, IconLink, IconNote, IconPencil, IconPointerFilled, IconReceipt, IconRocket, IconSend, IconSettings, IconSettings2, IconShieldCheck, IconShoppingCart, IconShoppingCartPlus, IconTrash, IconTruckDelivery, IconUsers, IconWorld, IconX } from '@tabler/icons-react'
import { MascotIcon } from '../../../lib/mascotIcon'
import { ProfileAvatarContent, VelticaMascot } from '@webbuilder/ui'
import { formatNumber } from '../../../lib/numbers'
import { useDragScroll } from '../../../lib/useDragScroll'

/** Filas que enseñan las tarjetas de resumen del panel. */
const WIDGET_ROWS = 12

const panel = {
  background: 'var(--dash-card-muted)',
  border: '1px solid',
  borderTopColor: 'var(--dash-card-muted-border-top)',
  borderRightColor: 'var(--dash-card-muted-border-side)',
  borderBottomColor: 'var(--dash-card-muted-border-bottom)',
  borderLeftColor: 'var(--dash-card-muted-border-side)',
  boxShadow: 'var(--dash-card-muted-shadow)',
  /* Un panel interno debe seguir la curva del widget padre de 16px sin verse
     más redondo que él. */
  borderRadius: 12,
} as const

const widgetPanel = { ...panel, borderRadius: 16 } as const

/* Un panel DENTRO de un widget. Se le quita la sombra —el widget que lo
   contiene ya trae la suya, y dos profundidades encajadas ensucian— pero
   entonces el filo tiene que dibujarlo el borde: `--dash-card-muted-border-*`
   es transparente y toda la definición de `panel` vive en esa sombra, así que
   sin esto la ficha queda blanca sobre blanco y no se ve dónde empieza. */
const innerPanel = {
  ...panel,
  borderTopColor: 'var(--dash-card-muted-border)',
  borderRightColor: 'var(--dash-card-muted-border)',
  borderBottomColor: 'var(--dash-card-muted-border)',
  borderLeftColor: 'var(--dash-card-muted-border)',
  boxShadow: 'none',
} as const

function pathLabel(path: string): string {
  /* Aquí no se está nombrando nada: se está ENSEÑANDO la ruta «/» en una lista
     de analíticas. Eso es interfaz y va en el idioma de quien mira, aunque la
     página se llame de otra forma. */
  if (!path || path === '/') return translate('pages.home')
  const last = path.replace(/\/+$/, '').split('/').pop() ?? path
  const words = last.replace(/-/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : path
}

/**
 * «hace 21 horas» / «21 hours ago».
 *
 * Delega en el formateador de `@webbuilder/i18n` en vez de armar el suyo: esta
 * copia pasaba `'es'` a mano a `Intl.RelativeTimeFormat`, así que decía «hace 21
 * horas» aunque el panel entero estuviera en inglés. Las dos implementaciones
 * eran además la misma lista de unidades, escrita dos veces.
 */
function relativeProjectTime(iso: string | null | undefined): string {
  return formatters(activeLanguage()).relative(iso) ?? ''
}

function trend(current: number, previous: number): { label: string; up: boolean } | null {
  // No comparison data → no badge (previously showed "Nuevo", which was noise).
  if (previous === 0) return null
  const pct = Math.round(((current - previous) / previous) * 100)
  if (pct === 0) return null
  return { label: `${pct > 0 ? '+' : ''}${pct}%`, up: pct > 0 }
}

function userGreeting(userName: string): string {
  const firstName = userName.trim().split(/\s+/)[0] || translate('common.user')
  const hour = new Date().getHours()
  /* Las tres franjas se reparten distinto en cada idioma —el inglés no tiene
     «buenas tardes» hasta las seis— pero eso lo decide el catálogo con su
     propia frase, no este reloj. Aquí sólo se elige cuál de las tres. */
  const clave = hour >= 5 && hour < 12
    ? 'greet.morning'
    : hour >= 12 && hour < 19
      ? 'greet.afternoon'
      : 'greet.evening'
  return translate(clave, { name: firstName })
}

/**
 * El saludo cuando se acaba de llegar cambiando de proyecto.
 *
 * Sustituye al de la hora, no lo acompaña. Quien acaba de cambiar de sitio no
 * necesita que le den los buenos días: necesita que le confirmen a DÓNDE llegó,
 * porque el panel entero se ve igual con un proyecto que con otro y el nombre
 * está en la esquina de abajo del menú, que es donde nadie mira al aterrizar.
 * Es la única vez que este globo tiene algo más urgente que decir que la hora.
 */
function switchGreeting(projectName: string): string {
  return translate('overview.switchGreeting', { name: projectName })
}

/** Y lo que significa estar en él, que es lo que hace útil la confirmación. */
const switchLine = () => ({ text: translate('overview.switchLine') })

function StatCard({ label, value, icon: Icon, change, foot, loading = false }: {
  label: string
  /** Texto, o `MoneyMetric` cuando la cifra es dinero. */
  value: ReactNode
  icon: typeof IconUsers
  change?: { label: string; up: boolean } | null
  /**
   * Una línea bajo la cifra, para lo que no es una variación.
   *
   * Las estadísticas comparan contra el periodo anterior y les basta `change`.
   * El dinero no se compara con nada: lo que hace falta decir de un saldo es
   * cuándo llega o de dónde sale, y eso no cabe en una flecha con porcentaje.
   */
  foot?: string
  loading?: boolean
}) {
  /* El relleno y la cabecera salen a la hoja de estilos —eran `style` en línea—
     porque el widget de Depósitos los aprieta cuando va a media rejilla, y un
     `style` en línea sólo se puede pisar con `!important`. */
  return (
    <div className="dash-stat-card" style={innerPanel}>
      <div className="dash-stat-card-head">
        <span className="dash-card-meta" style={{ fontWeight: 500 }}>{label}</span>
        <span className="dash-widget-glass-icon dash-stat-glass-icon" aria-hidden="true">
          <Icon size={14} />
        </span>
      </div>
      <div className="dash-overview-stat-value">
        {loading ? (
          <span className="sk dash-overview-stat-skeleton" />
        ) : (
          <span
            /* Un texto suelto se escala aquí; `MoneyMetric` trae su propia
               escala porque sabe separar la cifra de la moneda. */
            className="dash-loaded-content-in dash-card-metric dash-analytics-metric"
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={typeof value === 'string' ? value : undefined}
          >{value}</span>
        )}
        {!loading && change ? (
          <span className="dash-status-label" style={{ color: change.up ? 'var(--dash-accent)' : 'var(--dash-muted)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {change.up ? <IconArrowUpRight size={12} /> : <IconArrowDownRight size={12} />}
            {change.label}
          </span>
        ) : null}
      </div>
      {/* Debajo de la cifra y no al lado: `change` va en la misma línea porque
          es media palabra —«-26%»—, y una frase ahí dentro empujaría el número
          hasta partirlo. */}
      {!loading && foot ? <span className="dash-stat-card-foot">{foot}</span> : null}
    </div>
  )
}


function CopyLinkButton({ value }: { value: string }) {
  const { t } = useI18n()
  const [copied, setCopied] = useState(false)
  const resetTimerRef = useRef<number | null>(null)

  useEffect(() => () => {
    if (resetTimerRef.current !== null) window.clearTimeout(resetTimerRef.current)
  }, [])

  function copyLink() {
    void navigator.clipboard?.writeText(value)
    setCopied(true)
    if (resetTimerRef.current !== null) window.clearTimeout(resetTimerRef.current)
    resetTimerRef.current = window.setTimeout(() => setCopied(false), 1600)
  }

  return (
    <button
      type="button"
      className={`dash-copy-link-btn${copied ? ' is-copied' : ''}`}
      title={t(copied ? 'overview.linkCopied' : 'overview.copyLink')}
      aria-label={t(copied ? 'overview.linkCopied' : 'overview.copyLink')}
      onClick={copyLink}
    >
      <span className="dash-copy-link-icon" aria-hidden="true">
        <IconCheck size={13} className={copied ? 'is-visible' : ''} />
        <IconCopy size={13} className={copied ? '' : 'is-visible'} />
      </span>
    </button>
  )
}

/** "Hoy" / "Ayer" / la fecha: en una tarjeta de resumen la hora exacta sobra. */
function relativeDay(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return translate('time.today')
  if (days === 1) return translate('time.yesterday')
  if (days < 7) return translate('time.daysAgo', { n: days })
  return new Date(iso).toLocaleDateString(intlLocale(activeLanguage()), { day: 'numeric', month: 'short' })
}

/** Etiqueta compacta para contadores periódicos del widget de uso. */
function resetLabel(iso: string): string {
  const remainingMs = new Date(iso).getTime() - Date.now()
  const totalMinutes = Math.max(0, Math.ceil(remainingMs / 60_000))
  const days = Math.floor(totalMinutes / 1_440)
  if (days > 0) return translate(days === 1 ? 'time.inOneDay' : 'time.inNDays', { n: days })
  const hours = Math.floor(totalMinutes / 60)
  if (hours > 0) return `en ${hours} ${hours === 1 ? 'hora' : 'horas'}`
  return totalMinutes > 0 ? `en ${totalMinutes} min` : 'pronto'
}

/** Contador de uso con donut radial: avisa desde el 80% y marca el 100%. */
function UsageBar({ label, icon: Icon, resource, plan, used, limit, format, resetAt }: {
  label: string
  icon: typeof IconUsers
  /** Qué contador es: de ahí sale la explicación del aviso al pasar el ratón. */
  resource: UsageResource
  plan: BillingPlan
  used: number
  limit: number | null
  format: (value: number) => string
  /** Sólo en los contadores por periodo: los del proyecto no se reinician. */
  resetAt?: string | undefined
}) {
  const { t } = useI18n()
  const rawRatio = limit && limit > 0 ? used / limit : 0
  const ratio = Math.min(1, rawRatio)
  const state = usageState(used, limit)
  const status = state === 'full' ? 'limit' : state === 'warn' ? 'warning' : 'normal'
  // El aviso no se limita a decir que hay un aviso: el tooltip cuenta qué deja
  // de funcionar al llenarse: un "!" a secas obliga a ir a buscarlo a otro sitio.
  const alertMessage = usageAlertMessage(resource, state, plan, rawRatio, t)
  const percentage = limit === null ? 0 : Math.round(ratio * 100)
  return (
    <div className={`dash-usage-row is-${status}`}>
      <div className="dash-usage-row-head">
        <span className="dash-widget-glass-icon dash-stat-glass-icon dash-usage-row-icon" aria-hidden="true">
          <Icon size={14} />
        </span>
        <div className="dash-usage-row-copy">
          {/* El aviso va fuera de la etiqueta y no dentro: el nombre se recorta
              con puntos suspensivos, y lo que recorta el nombre recortaba
              también el globo con la explicación. */}
          <span className="dash-usage-row-title">
            <span className="dash-usage-row-label">{label}</span>
            {alertMessage ? (
              <span className={`dash-usage-alert is-${state}`} role="img" aria-label={alertMessage} data-tooltip={alertMessage}>
                <IconAlertCircle size={13} aria-hidden="true" />
              </span>
            ) : null}
          </span>
          <span className="dash-usage-row-values">
            <b>{format(used)}</b>
            {limit === null ? <span>{t('overview.unlimited')}</span> : <span>{t('overview.ofLimit', { limit: format(limit) })}</span>}
          </span>
          {resetAt ? <em>{t('overview.resetsAt', { when: resetLabel(resetAt) })}</em> : null}
        </div>
        <span
          className="dash-usage-row-donut"
          style={{ '--dash-widget-usage-progress': `${percentage * 3.6}deg` } as CSSProperties}
          aria-label={limit === null ? 'Uso ilimitado' : `${percentage}% utilizado`}
        >
          {limit === null ? '∞' : `${percentage}%`}
        </span>
      </div>
    </div>
  )
}

function NoteAvatar({ name, url }: { name: string; url?: string | null | undefined }) {
  return (
    <span className="dash-note-avatar" aria-hidden="true">
      <ProfileAvatarContent name={name || '?'} src={url} />
    </span>
  )
}

/**
 * La tecla que se nombra en las ayudas, según el teclado de quien mira.
 *
 * El manejador acepta las dos desde siempre; lo que estaba mal era el rótulo,
 * que prometía ⌘ a todo el mundo — en Windows y Linux se lee como una función
 * que no existe.
 */
const COMMAND_KEY = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
  ? '\u2318'
  : 'Ctrl'

function ProjectNotes({
  projectId,
  token,
  initial,
  currentUser,
  canWrite,
  canModerate,
  loading,
}: {
  projectId: string
  token: string
  initial: string
  currentUser?: { id: string; name: string; avatarUrl?: string | null } | undefined
  /** Un invitado con rol de lectura ve las notas, pero no el compositor. */
  canWrite: boolean
  /** Quien lleva el equipo puede retirar la nota de cualquiera. */
  canModerate: boolean
  /** Mientras el proyecto carga, `initial` todavía no son las notas de nadie. */
  loading: boolean
}) {
  const { t } = useI18n()
  /* El rótulo de la nota heredada, en un ref y no leído dentro de los efectos:
     así la lista no se rehace al cambiar de idioma —eso borraría lo que se
     estuviera escribiendo— y el mismo nombre vale también al fundir. */
  const legacyLabel = useRef(t('overview.notes.initialNote'))
  legacyLabel.current = t('overview.notes.initialNote')
  const [items, setItems] = useState<ProjectNoteItem[]>(() => parseNotes(initial, legacyLabel.current))
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const [full, setFull] = useState(false)
  const [teamAvatars, setTeamAvatars] = useState<Record<string, string>>({})

  /**
   * Avatares de los miembros del equipo obtenidos directamente de la API del proyecto.
   * Esto garantiza que aunque las notas se hayan creado antes de que un miembro
   * subiera su foto, su foto actual se muestre en todas sus notas.
   */
  useEffect(() => {
    if (!projectId || !token) return
    let alive = true
    fetchTeam(projectId, token)
      .then((snap) => {
        if (!alive) return
        const map: Record<string, string> = {}
        if (snap.owner?.id && snap.owner.avatarUrl) {
          map[snap.owner.id] = snap.owner.avatarUrl
          if (snap.owner.name) map[`name:${snap.owner.name.trim().toLowerCase()}`] = snap.owner.avatarUrl
        }
        for (const m of snap.members || []) {
          if (m.id && m.avatarUrl) {
            map[m.id] = m.avatarUrl
            if (m.name) map[`name:${m.name.trim().toLowerCase()}`] = m.avatarUrl
          }
        }
        setTeamAvatars(map)
      })
      .catch(() => {
        // Silencioso si falla la red o permisos
      })
    return () => {
      alive = false
    }
  }, [projectId, token])

  /**
   * Resuelve el avatar definitivo para cualquier nota:
   * 1. Usuario actual (`currentUser`): si la nota la escribió el usuario en sesión y tiene foto, siempre se usa su foto actual.
   * 2. Miembro del equipo (`teamAvatars`): si la API del equipo conoce su avatar, se usa.
   * 3. El `userAvatar` registrado en la nota (que ya propaga el más reciente de ese usuario).
   */
  const resolveAvatar = useCallback((note: ProjectNoteItem): string | null | undefined => {
    const isMe = Boolean(
      (currentUser?.id && note.userId === currentUser.id) ||
      (currentUser?.name && note.userName && note.userName.trim().toLowerCase() === currentUser.name.trim().toLowerCase())
    )
    if (isMe && currentUser?.avatarUrl) {
      return currentUser.avatarUrl
    }
    if (note.userId && teamAvatars[note.userId]) {
      return teamAvatars[note.userId]
    }
    if (note.userName) {
      const key = `name:${note.userName.trim().toLowerCase()}`
      if (teamAvatars[key]) return teamAvatars[key]
    }
    return note.userAvatar || null
  }, [currentUser, teamAvatars])

  /**
   * Lo que he borrado yo en esta sesión.
   *
   * Al fundir lo mío con lo guardado hay que distinguir «esta nota la borré» de
   * «esta nota no la había visto todavía»: sin la lista, cada guardado
   * resucitaría lo que acabo de eliminar, porque en el servidor sigue estando.
   */
  const deletedIds = useRef<Set<string>>(new Set())

  /** El campo de escribir, para poder medirlo. */
  const composerRef = useRef<HTMLTextAreaElement | null>(null)

  /**
   * El alto del compositor lo pone lo escrito.
   *
   * Antes lo ponía el usuario con el tirador de la esquina, y en reposo eran dos
   * renglones vacíos esperando. Se mide en dos tiempos —a `auto` primero— porque
   * `scrollHeight` con un alto ya puesto devuelve ese alto y el campo no podría
   * volver a encoger al borrar texto ni al vaciarse tras publicar.
   *
   * El tope lo pone `max-height` en la hoja de estilos, no esto: pasado ese
   * punto el alto en línea se queda largo, el CSS lo recorta y la nota kilométrica
   * se desplaza por dentro en vez de empujar la lista fuera de la pantalla.
   */
  useEffect(() => {
    const field = composerRef.current
    if (!field) return
    field.style.height = 'auto'
    field.style.height = `${field.scrollHeight}px`
    /* Y el mismo alto, publicado como variable.
       En móvil el campo se declara a 16px —para que Safari no haga zoom al
       enfocarlo— y se encoge con `transform` hasta el tamaño que se ve. Una
       transformación no cambia lo que el campo OCUPA, así que debajo queda un
       hueco proporcional a su alto, y ese alto sólo se conoce aquí: lo pone
       este efecto. La hoja de estilos lo recoge para restarlo. En escritorio la
       variable existe igual y no la lee nadie. */
    field.style.setProperty('--ios-h', `${field.scrollHeight}px`)
  }, [draft])

  useEffect(() => {
    setItems(parseNotes(initial, legacyLabel.current))
    setDraft('')
    setEditingId(null)
    setDeletingId(null)
    setFailed(false)
    setFull(false)
    deletedIds.current = new Set()
  }, [initial, projectId])

  /**
   * Guardar es leer, fundir y escribir — en ese orden.
   *
   * La columna se reemplaza entera en cada PATCH, así que mandar directamente
   * lo que tengo en pantalla borra lo que un compañero haya escrito mientras
   * tanto. Se relee lo guardado, se funde (ver `mergeNotes`) y se manda el
   * resultado, que además es lo que queda en pantalla: así el widget enseña las
   * notas de los demás en cuanto uno escribe la suya.
   *
   * Si la relectura falla se sigue adelante con lo propio: perder la nota que
   * el usuario acaba de escribir por un fallo de red sería peor que el riesgo
   * de pisar a alguien.
   */
  const persist = useCallback((next: ProjectNoteItem[]) => {
    setSaving(true)
    void (async () => {
      let merged = next
      try {
        const stored = await getProjectNotes(token, projectId)
        merged = mergeNotes(parseNotes(stored.notes, legacyLabel.current), next, deletedIds.current)
      } catch {
        // Sin relectura, se guarda lo propio tal cual.
      }
      merged = merged.map((item) => {
        const resolved = resolveAvatar(item)
        if (resolved && !item.userAvatar) {
          return { ...item, userAvatar: resolved }
        }
        return item
      })
      if (!notesFit(merged)) {
        setSaving(false)
        setFull(true)
        return
      }
      try {
        await updateProjectNotes(token, projectId, serializeNotes(merged))
        setItems(merged)
        setFailed(false)
      } catch {
        setFailed(true)
      }
      setSaving(false)
    })()
  }, [token, projectId, resolveAvatar])

  const handleAdd = () => {
    const text = draft.trim().slice(0, NOTE_TEXT_MAX)
    if (!text || saving) return
    const newNote: ProjectNoteItem = {
      id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `note-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      userId: currentUser?.id ?? '',
      userName: currentUser?.name || t('overview.notes.unnamed'),
      userAvatar: currentUser?.avatarUrl ?? null,
      text,
      createdAt: new Date().toISOString(),
    }
    const next = [newNote, ...items].map((item) => {
      const resolved = resolveAvatar(item)
      if (resolved && !item.userAvatar) {
        return { ...item, userAvatar: resolved }
      }
      return item
    })
    /* El tope se comprueba ANTES de tocar la pantalla: si no cabe, la nota no
       se da por escrita ni se limpia el compositor, que es lo que permite
       copiarla a otro sitio antes de hacer hueco. */
    if (!notesFit(next)) {
      setFull(true)
      return
    }
    setItems(next)
    setDraft('')
    setFull(false)
    persist(next)
  }

  const handleStartEdit = (note: ProjectNoteItem) => {
    setEditingId(note.id)
    setEditText(note.text)
    setDeletingId(null)
  }

  const handleSaveEdit = (noteId: string) => {
    const text = editText.trim().slice(0, NOTE_TEXT_MAX)
    if (!text) return
    const next = items.map((item) => (
      item.id === noteId
        ? { ...item, text, updatedAt: new Date().toISOString() }
        : item
    ))
    if (!notesFit(next)) {
      setFull(true)
      return
    }
    setItems(next)
    setEditingId(null)
    setFull(false)
    persist(next)
  }

  const handleDelete = (noteId: string) => {
    deletedIds.current.add(noteId)
    const next = items.filter((item) => item.id !== noteId)
    setItems(next)
    setDeletingId(null)
    setFull(false)
    persist(next)
  }

  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, noteId: string) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSaveEdit(noteId)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditingId(null)
    }
  }

  /* Cuánto queda de la columna, y no de la nota: el aviso sale cuando el
     conjunto se acerca al tope, que es cuando hay algo que decidir. La cuenta
     se hacía con una constante importada que no se usaba en ninguna parte, así
     que nadie veía venir el tope hasta que el guardado empezaba a fallar. */
  const roomLeft = NOTES_MAX - serializeNotes(items).length
  const showRoom = roomLeft < NOTES_MAX / 5

  /* Mientras el proyecto carga, `initial` viene vacío aunque haya notas
     guardadas: el panel pinta desde su caché, que no las lleva. Enseñar el
     vacío ahí no sólo miente — si alguien escribe en ese hueco, lo que se
     guarda sale de una lista vacía. */
  if (loading) {
    return (
      <div className="dash-notes-feed-widget">
        <div className="dash-notes-loading" aria-hidden="true">
          <span className="dash-notes-skeleton-line" />
          <span className="dash-notes-skeleton-line is-short" />
        </div>
      </div>
    )
  }

  return (
    <div className={`dash-notes-feed-widget${items.length === 0 ? ' is-empty' : ''}`}>
      {/* Compositor superior.
          `has-draft` mantiene abajo el atajo y el botón cuando hay texto sin
          publicar y el foco se ha ido a otra parte: el `:focus-within` de la
          hoja de estilos cubre el rato que se escribe, pero sin esto una nota a
          medias perdía su botón en cuanto pulsabas fuera. */}
      {canWrite ? (
        <div className={`dash-notes-composer${draft.trim() ? ' has-draft' : ''}`}>
          <div className="dash-notes-composer-row">
            {/* Sin el avatar de quien escribe: aquí no identifica a nadie —el
                único que puede estar escribiendo es quien mira la pantalla— y
                sólo le quitaba ancho al campo. En las notas ya publicadas sí se
                queda: ahí dice quién la dejó, que es la mitad de lo que aporta
                una nota de equipo. */}
            <div className="dash-notes-composer-input-box">
              <textarea
                ref={composerRef}
                className="dash-notes-composer-textarea"
                placeholder={t('overview.notes.placeholder')}
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setFull(false) }}
                onKeyDown={handleComposerKeyDown}
                maxLength={NOTE_TEXT_MAX}
                rows={1}
              />
              <div className="dash-notes-composer-bar">
                <span className="dash-notes-kbd-hint">{t('overview.notes.keyboardHint', { key: COMMAND_KEY })}</span>
                <button
                  type="button"
                  className="dash-notes-submit-btn"
                  disabled={!draft.trim() || saving}
                  onClick={handleAdd}
                >
                  <IconSend size={13} />
                  <span>{t('overview.notes.add')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {full ? (
        <div className="dash-notes-error-pill">
          {t('overview.notes.full')}
        </div>
      ) : failed ? (
        <div className="dash-notes-error-pill">
          {t('overview.notes.saveFailed')}
        </div>
      ) : showRoom ? (
        <div className="dash-notes-room-hint">
          {t('overview.notes.charsLeft', { count: Math.max(0, roomLeft) })}
        </div>
      ) : null}

      {/*
        Lista de notas. Vacía, no se dice nada.

        Había un icono, «todavía no hay notas» y una frase explicando para qué
        sirven. Tres renglones para describir un widget que se llama «Notas del
        equipo» y que tiene encima el hueco donde se escribe una: quien lo mira
        ya sabe qué es y ya ve por dónde empezar. Un estado vacío sólo hace falta
        donde lo vacío se puede confundir con lo roto, y aquí no.
      */}
      <div className="dash-notes-stream">
        {items.length === 0 ? null : (
          items.map((note) => {
            const isMe = Boolean(currentUser?.id && note.userId === currentUser.id)
            const isEditing = editingId === note.id
            const isConfirmingDelete = deletingId === note.id
            /* Cada quien responde de lo suyo, y quien lleva el equipo puede
               retirar cualquier cosa. Antes bastaba con poder escribir para
               reescribir la nota de otro —bajo su nombre y sin decir quién la
               tocó—, y una bitácora en la que eso pasa deja de servir como
               bitácora. Para volver al comportamiento anterior, basta con
               dejar aquí `canWrite`. */
            const canManage = canWrite && (isMe || !note.userId || canModerate)

            return (
              <article key={note.id} className="dash-note-card">
                <div className="dash-note-card-head">
                  <div className="dash-note-card-author">
                    <NoteAvatar name={note.userName} url={resolveAvatar(note)} />
                    <span className="dash-note-author-name">{note.userName || t('overview.notes.unnamed')}</span>
                    {isMe ? <span className="dash-note-you-pill">{t('overview.notes.you')}</span> : null}
                    {note.createdAt ? (
                      <>
                        <span className="dash-note-dot">·</span>
                        <span className="dash-note-time">{relativeProjectTime(note.createdAt)}</span>
                      </>
                    ) : null}
                    {note.updatedAt ? (
                      <span className="dash-note-edited">({t('overview.notes.edited')})</span>
                    ) : null}
                  </div>

                  {!isEditing && canManage ? (
                    <div className="dash-note-card-actions">
                      {isConfirmingDelete ? (
                        <div className="dash-note-confirm-delete">
                          <span>{t('overview.notes.deleteConfirm')}</span>
                          <button
                            type="button"
                            className="dash-note-btn-danger"
                            onClick={() => handleDelete(note.id)}
                            aria-label={t('overview.notes.delete')}
                            title={t('overview.notes.delete')}
                          >
                            <IconCheck size={15} />
                          </button>
                          <button
                            type="button"
                            className="dash-note-btn-ghost"
                            onClick={() => setDeletingId(null)}
                            aria-label={t('overview.notes.cancel')}
                            title={t('overview.notes.cancel')}
                          >
                            <IconX size={15} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="dash-note-action-btn"
                            aria-label={t('overview.notes.edit')}
                            title={t('overview.notes.edit')}
                            onClick={() => handleStartEdit(note)}
                          >
                            <IconPencil size={16} />
                          </button>
                          <button
                            type="button"
                            className="dash-note-action-btn is-danger"
                            aria-label={t('overview.notes.delete')}
                            title={t('overview.notes.delete')}
                            onClick={() => setDeletingId(note.id)}
                          >
                            <IconTrash size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  ) : null}
                </div>

                {isEditing ? (
                  <div className="dash-note-edit-area">
                    <textarea
                      className="dash-note-edit-textarea"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, note.id)}
                      maxLength={NOTE_TEXT_MAX}
                      rows={3}
                      autoFocus
                    />
                    <div className="dash-note-edit-bar">
                      <span className="dash-notes-kbd-hint">{t('overview.notes.editHint', { key: COMMAND_KEY })}</span>
                      <div className="dash-note-edit-btns">
                        <button
                          type="button"
                          className="dash-notes-btn-secondary"
                          onClick={() => setEditingId(null)}
                        >
                          {t('overview.notes.cancel')}
                        </button>
                        <button
                          type="button"
                          className="dash-notes-submit-btn"
                          disabled={!editText.trim() || saving}
                          onClick={() => handleSaveEdit(note.id)}
                        >
                          {t('overview.notes.save')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="dash-note-card-body">{note.text}</div>
                )}
              </article>
            )
          })
        )}
      </div>
    </div>
  )
}

type SetupStep = {
  key: string
  label: string
  description: string
  /**
   * El icono del paso, en blanco y en medio de la cabecera ilustrada.
   *
   * Antes era un cuadradito de cristal de 34 píxeles, el mismo que llevan el
   * menú y los vacíos. Servía para señalar de qué área es cada paso, pero esto
   * no es una fila más del panel: es lo primero que ve quien acaba de crear su
   * sitio y no sabe qué sigue, y tres cuadraditos iguales no invitan a empezar.
   */
  icon: typeof IconRocket
  done: boolean
  cta: string
  onAction: () => void
}

/**
 * Si la última vez este proyecto tenía algún paso pendiente.
 *
 * El hueco que se reserva mientras cargan los datos existe para que la bandeja
 * no empuje el panel al aparecer. Pero se reservaba SIEMPRE que los pasos no
 * estuvieran descartados —sin mirar si quedaba alguno por hacer—, así que a
 * quien ya había terminado los tres le salía el esqueleto en cada carga y se
 * quedaba ahí hasta que llegaban los datos, para no enseñar nada. Un hueco
 * guardado para algo que nunca iba a llegar, y además de lo último en irse.
 *
 * No se puede saber sin los datos, pero sí se puede recordar: la vez anterior
 * ya se supo. Se guarda esa respuesta y se usa para decidir si vale la pena
 * reservar. Un proyecto del que no consta nada reserva igual que antes — es la
 * primera visita, justo cuando el hueco sí hace falta.
 */
const SETUP_PENDING_STORAGE_PREFIX = 'veltica:setup-had-pending:'

function readSetupHadPending(projectId: string): boolean {
  try {
    const raw = window.localStorage.getItem(`${SETUP_PENDING_STORAGE_PREFIX}${projectId}`)
    /* Sin nada guardado se reserva: es la primera vez que se abre. */
    return raw === null ? true : raw === '1'
  } catch {
    return true
  }
}

function writeSetupHadPending(projectId: string, pending: boolean): void {
  try {
    window.localStorage.setItem(`${SETUP_PENDING_STORAGE_PREFIX}${projectId}`, pending ? '1' : '0')
  } catch {
    /* Almacenamiento bloqueado: se reservará de más, que es lo inocuo. */
  }
}

/** Cada paso cerrado vuelve a estar disponible seis horas después. */
const SETUP_STEP_DISMISS_MS = 6 * 60 * 60 * 1_000
const SETUP_STEP_DISMISS_STORAGE_PREFIX = 'veltica:setup-step-dismissals:'

type SetupStepDismissals = Record<string, number>

function setupStepDismissalStorageKey(projectId: string): string {
  return `${SETUP_STEP_DISMISS_STORAGE_PREFIX}${projectId}`
}

/**
 * Recupera sólo cierres vigentes. Además limpia los vencidos para que el
 * almacenamiento no conserve para siempre pasos que ya volvieron a aparecer.
 */
function readSetupStepDismissals(projectId: string): SetupStepDismissals {
  try {
    const storageKey = setupStepDismissalStorageKey(projectId)
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      window.localStorage.removeItem(storageKey)
      return {}
    }

    const now = Date.now()
    const active = Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, number] => (
        typeof entry[1] === 'number'
        && Number.isFinite(entry[1])
        && now - entry[1] < SETUP_STEP_DISMISS_MS
      )),
    )

    if (Object.keys(active).length === 0) window.localStorage.removeItem(storageKey)
    else if (Object.keys(active).length !== Object.keys(parsed).length) {
      window.localStorage.setItem(storageKey, JSON.stringify(active))
    }
    return active
  } catch {
    /* Sin almacenamiento o con datos inválidos: se enseñan todos los pasos. */
    return {}
  }
}

function writeSetupStepDismissals(projectId: string, dismissals: SetupStepDismissals): void {
  try {
    window.localStorage.setItem(setupStepDismissalStorageKey(projectId), JSON.stringify(dismissals))
  } catch {
    /* El estado de esta sesión basta; al recargar volverán a aparecer. */
  }
}

/**
 * La miniatura de un paso, como las de «Desde un template» y «En blanco» de la
 * pantalla de inicio: un marco blanco pequeño con el icono del paso y el cursor
 * yendo a pulsarlo. Antes era una cabecera ilustrada de filo a filo, de 120 px
 * de alto: cuatro seguidas se comían media pantalla, y en el teléfono, una
 * debajo de otra, mucho más.
 *
 * Hace lo mismo que el botón del pie: es lo primero que se mira, y tiene que
 * poder pulsarse.
 */
function SetupStepArt({ icon: Glyph, label, onClick, disabled }: {
  icon: typeof IconRocket
  label: string
  onClick: () => void
  disabled: boolean
}) {
  return (
    <button
      type="button"
      className="dash-setup-tile"
      aria-label={label}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      <span className="dash-setup-tile-frame" aria-hidden="true">
        <span className="dash-setup-tile-canvas"><Glyph size={20} stroke={1.8} /></span>
        <IconPointerFilled size={13} className="dash-setup-tile-cursor" />
      </span>
    </button>
  )
}

/**
 * Los primeros pasos del proyecto como una ruta breve hacia publicación.
 *
 * El encabezado resume el avance y cada paso conserva su propio espacio, color
 * y acción. Los pendientes se ordenan primero; cuando uno se completa pasa a la
 * derecha y baja de intensidad sin desaparecer, para que el progreso sea fácil
 * de entender sin convertir el bloque en una lista de avisos.
 */
function SetupStepsRow({ steps, onDismiss }: { steps: SetupStep[]; onDismiss: (key: string) => void }) {
  const { t } = useI18n()
  const completed = steps.filter((step) => step.done).length
  /* Un carril que se desliza, no una rejilla: en el iPad y en el teléfono las
     tarjetas iban una debajo de otra y el bloque se comía la pantalla. Las
     flechas dicen que hay más y hacia dónde; los bordes se difuminan del lado
     en que queda algo. */
  const railRef = useRef<HTMLDivElement>(null)
  useDragScroll(railRef)
  const [edges, setEdges] = useState({ prev: false, next: false })
  useLayoutEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth
      setEdges({ prev: rail.scrollLeft > 2, next: rail.scrollLeft < max - 2 })
    }
    update()
    rail.addEventListener('scroll', update, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    observer?.observe(rail)
    return () => { rail.removeEventListener('scroll', update); observer?.disconnect() }
  }, [steps])
  const slide = (direction: 1 | -1) => {
    const rail = railRef.current
    const card = rail?.querySelector<HTMLElement>('.dash-setup-note')
    if (!rail || !card) return
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0
    rail.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' })
  }

  return (
    /* La animación de entrada va en el bloque, no en cada tarjeta.
       Con `dash-loaded-content-in` en las tres, cada una entraba con su propio
       retardo escalonado —cuarenta milisegundos de diferencia— justo después de
       sustituir al hueco de carga: tres apariciones seguidas donde los widgets
       de al lado hacen una sola. Escalonar sirve en una lista larga, para que se
       lea como que el contenido se posa; en tres cajas que ya estaban dibujadas
       en gris, sólo se nota como parpadeo. */
    <section className="dash-setup-inbox dash-loaded-content-in" aria-label={t('overview.setup.aria')}>
      <header className="dash-setup-inbox-head">
        <div>
          <span>{t('overview.setup.eyebrow')}</span>
          <strong>{t('overview.setup.title')}</strong>
        </div>
        {/* Cuántos faltan, no cuántos van: los hechos no se dibujan, y un
            «2 de 3» con una sola tarjeta contaba pasos que no se veían. */}
        <div className="dash-setup-progress">
          <span>{steps.length - completed === 1
            ? t('overview.setup.leftOne')
            : t('overview.setup.leftMany', { count: steps.length - completed })}</span>
        </div>
        {edges.prev || edges.next ? (
          <div className="dash-setup-arrows">
            <button type="button" className="dash-setup-arrow" aria-label={t('overview.setup.prev')} disabled={!edges.prev} onClick={() => slide(-1)}>
              <IconChevronLeft size={15} aria-hidden="true" />
            </button>
            <button type="button" className="dash-setup-arrow" aria-label={t('overview.setup.next')} disabled={!edges.next} onClick={() => slide(1)}>
              <IconChevronRight size={15} aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </header>

      <div ref={railRef} className={`dash-setup-grid${edges.prev ? ' can-prev' : ''}${edges.next ? ' can-next' : ''}`}>
      {/* Sólo lo que falta: lo hecho ya cuenta en la barra («2 de 4») y una
          tarjeta con «Hecho» sólo ocupaba sitio. */}
      {steps.filter((step) => !step.done).map((step) => {
        return (
          <article
            key={step.key}
            className={`dash-setup-note${step.done ? ' is-done' : ''}`}
          >
            <div className="dash-setup-note-top">
              {/* La miniatura ejecuta la misma acción que el botón del pie:
                  es lo primero que se mira y tiene que poder pulsarse. */}
              <SetupStepArt
                icon={step.icon}
                label={`${step.cta}: ${step.label}`}
                onClick={step.onAction}
                disabled={step.done}
              />
              <button
                className="dash-setup-note-dismiss"
                type="button"
                aria-label={t('overview.hideStep', { name: step.label })}
                onClick={() => onDismiss(step.key)}
              >
                <IconX size={14} />
              </button>
            </div>

            {/* La tipografía de una tarjeta del panel, con sus clases: título
                y descripción iban un escalón por debajo —letra de fila de
                lista— así que al lado de un widget parecían de otra pantalla. */}
            <div className="dash-setup-note-copy">
              <h3 className="dash-card-title">{step.label}</h3>
              <p className="dash-card-description">{step.description}</p>
            </div>

            <footer className="dash-setup-note-footer">
              {step.done ? (
                <span className="dash-setup-note-done"><IconCheck size={13} /> {t('overview.setup.done')}</span>
              ) : (
                /* El botón blanco de siempre. Tenía uno propio de 32px con
                   esquinas de 9: parecido, pero no el mismo. */
                <button type="button" className="dash-secondary-btn dash-setup-note-action" onClick={step.onAction}>
                  {step.cta} <IconArrowUpRight size={14} aria-hidden="true" />
                </button>
              )}
            </footer>
          </article>
        )
      })}
      </div>
    </section>
  )
}

/** Más de cinco filas en un resumen ya no es un resumen. */
const MAX_SUMMARY_ROWS = 5

/**
 * Cuántos elementos caben en el resumen.
 *
 * El alto de la tarjeta lo marca el widget más alto de su fila en la rejilla,
 * no ella misma, así que casi siempre le sobra hueco por abajo. En vez de
 * dejarlo en blanco se enseñan tantos elementos como quepan.
 *
 * Se mide EL HUECO QUE SOBRA, no la altura disponible. La diferencia es lo que
 * evita el bucle: al medir la altura total, cada pasada añadía una fila, la
 * tarjeta crecía, la altura leída crecía con ella y volvía a caber una más —
 * hasta agotar los datos. El hueco, en cambio, tiende a cero según se llena, y
 * ahí el ajuste se detiene solo.
 *
 * También corrige a la baja: si el hueco sale negativo la lista se pasa del
 * marco y se quitan filas. `trunc` en vez de `floor` es lo que impide que al
 * corregir se pase de largo y empiece a oscilar entre dos tamaños.
 *
 * Y el resultado se redondea al ancho de la rejilla. En 4/4 las listas van a dos
 * columnas, y ahí el tope de cinco dejaba una tarjeta sola con un hueco al lado:
 * el ajuste sube de dos en dos —una fila son dos tarjetas— así que partiendo de
 * un número impar nunca llegaba a uno par. Completar esa última fila no cuesta
 * alto, porque la fila ya está ahí.
 */
function useRowCapacity(fallback: number) {
  const listRef = useRef<HTMLDivElement>(null)
  const [capacity, setCapacity] = useState(fallback)

  useLayoutEffect(() => {
    const node = listRef.current
    if (!node) return

    const measure = () => {
      const columnTemplate = getComputedStyle(node).gridTemplateColumns
      const columns = columnTemplate && columnTemplate !== 'none'
        ? Math.max(1, columnTemplate.split(/\s+/).filter(Boolean).length)
        : 1

      // Medido y no supuesto: el alto de una fila cambia con el ancho de la
      // tarjeta, con la miniatura y con el tamaño de letra del sistema.
      const rowHeight = node.querySelector<HTMLElement>('.dash-summary-item-row')?.offsetHeight ?? 0
      if (rowHeight <= 0) return

      const slack = node.clientHeight - node.scrollHeight
      const rows = Math.trunc(slack / rowHeight)

      setCapacity((current) => {
        const grown = Math.max(fallback, current + rows * columns)
        // Sin hueco que repartir todavía puede quedar una fila a medias, así que
        // el redondeo se aplica igual: por eso `rows === 0` no corta la medida.
        const next = Math.ceil(grown / columns) * columns
        return next === current ? current : next
      })
    }

    measure()
    /**
     * Una segunda medida tras el primer fotograma.
     *
     * En el montaje la rejilla aún no ha repartido alturas — todas las tarjetas
     * miden lo que su contenido — así que la primera lectura da el mínimo y,
     * si nada cambia de tamaño después, el observador no vuelve a dispararse y
     * la lista se queda corta para siempre.
     */
    const frame = requestAnimationFrame(measure)
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => { cancelAnimationFrame(frame); observer.disconnect() }
  }, [fallback])

  return { listRef, capacity }
}

type SummaryRow = {
  key: string
  label: string
  sub?: string
  /** Icono del dato secundario, p. ej. el tipo de oferta bajo su título. */
  subIcon?: ReactNode
  category?: {
    label: string
    icon?: ReactNode
  }
  thumbIcon?: ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  itemCount?: number
  /** Un importe ya formateado, al final del título. Lo usa Transacciones: en una
   *  venta la cifra es la mitad de la información, y en `sub` se perdía. */
  amount?: string
  /** Apagado: ese dinero no cuenta como entrada —se devolvió, o está retenido
   *  mientras se revisa una disputa—. */
  amountMuted?: boolean
  /** Al revés que `amountMuted`: en vez de apagarse, resalta. Lo usa la venta
   *  disputada, que es lo único de la lista que pide hacer algo. */
  amountAlert?: boolean
  /** La misma pastilla sin el verde de ingreso, para lo que no es dinero. */
  amountNeutral?: boolean
  /** La pastilla en el acento del panel, para el dato clave de la fila. */
  amountAccent?: boolean
  /** `sub` como pastilla y no como texto: lo usa «4 personas», que es un dato
   *  y no el nombre de nadie. Igual que el chip de la lista de Citas. */
  subChip?: boolean
  /** Estado que se conserva a la derecha, separado del dato secundario. */
  statusLabel?: string
  status?: string
  /** Lo que va bajo el importe, a la derecha del todo. En Transacciones, cuándo:
   *  así la fila se lee en dos por dos —qué y a quién, cuánto y cuándo— en vez
   *  de amontonar comprador y fecha en la misma línea. */
  subEnd?: string
  href?: string | null
  thumbnailUrl?: string | null
  unread?: boolean
  /** La página de inicio se distingue, igual que en la sección Páginas. */
  isHome?: boolean
  onClick?: () => void
  /**
   * Acciones propias de la fila, para el menú de los tres puntos.
   *
   * Van como dato y no como un caso especial dentro del menú: el widget sirve a
   * cuatro secciones y cada una sabe qué se puede hacer con lo suyo. Sin esto,
   * el menú tendría que preguntar de qué lista viene para decidir qué pintar.
   */
  actions?: SummaryRowAction[]
}

type SummaryRowAction = {
  key: string
  label: string
  icon: ReactNode
  onSelect: () => void
  /** Traza una línea encima, para separar bloques como en el resto de menús. */
  separatorBefore?: boolean
}

/** Acciones esenciales de una fila estrecha. Deliberadamente no incluye
 * eliminar: una acción destructiva pertenece a la pantalla de detalle. */
function SummaryRowMenu({ row, primaryLabel }: { row: SummaryRow; primaryLabel: string }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [open])

  const copyHref = () => {
    if (!row.href) return
    void navigator.clipboard?.writeText(row.href)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1400)
  }

  return (
    <div className="dash-summary-quarter-actions" ref={menuRef}>
      <button
        type="button"
        className="dash-summary-quarter-more"
        aria-label={t('overview.moreActionsFor', { name: row.label })}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <IconDots size={16} aria-hidden="true" />
      </button>
      {open ? (
        <div className="dash-summary-quarter-menu" role="menu">
          {row.actions?.length ? row.actions.map((action) => (
            <Fragment key={action.key}>
              {action.separatorBefore ? <div className="project-menu-rule" role="separator" /> : null}
              <button type="button" role="menuitem" onClick={() => { setOpen(false); action.onSelect() }}>
                {action.icon} {action.label}
              </button>
            </Fragment>
          )) : null}
          {row.actions?.length ? null : row.onClick ? (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); row.onClick?.() }}>
              <IconArrowUpRight size={14} aria-hidden="true" /> {primaryLabel}
            </button>
          ) : null}
          {row.actions?.length ? null : row.href ? (
            <>
              <a href={row.href} target="_blank" rel="noreferrer" role="menuitem" onClick={() => setOpen(false)}>
                <IconExternalLink size={14} aria-hidden="true" /> {t('overview.openLink')}
              </a>
              <button type="button" role="menuitem" onClick={copyHref}>
                {copied ? <IconCheck size={14} aria-hidden="true" /> : <IconCopy size={14} aria-hidden="true" />}
                {t(copied ? 'common.copied' : 'overview.copyLink')}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function SummaryCard({ icon: Icon, image, kind, title, count, unit, rows, action, emptyActionLabel, onAction, onEmptyAction }: {
  icon: typeof IconFileText
  /** Ilustración que sustituye al icono de línea cuando la tarjeta tiene una. */
  image?: string | undefined
  /**
   * Qué lista es, para saber qué dice el botón de cada fila.
   *
   * Antes se decidía comparando el TÍTULO contra «Páginas», «Contactos»… y eso
   * se rompió en cuanto el título pasó por el traductor: con el panel en inglés
   * ninguna comparación acertaba y todas las filas decían «Ver mensaje». Un
   * texto de pantalla no puede hacer de identificador.
   */
  kind: 'pages' | 'cms' | 'contacts' | 'transactions' | 'bookings' | 'messages' | 'offers' | 'blog'
  title: string
  count: number
  unit: string
  rows: SummaryRow[]
  action: string
  /** Enlace discreto que convierte el estado vacío en un siguiente paso. */
  emptyActionLabel?: string | undefined
  /** Lo que hace el botón del vacío, si no es lo mismo que «Ver todo» (crear, no listar). */
  onEmptyAction?: (() => void) | undefined
  onAction: () => void
}) {
  const { t } = useI18n()
  const { listRef, capacity } = useRowCapacity(MAX_SUMMARY_ROWS)
  const visibleRows = rows.slice(0, capacity)
  const remaining = Math.max(0, count - visibleRows.length)
  const rowActionLabel = t({
    pages: 'overview.row.openInEditor',
    cms: 'overview.row.openCollection',
    contacts: 'overview.row.openProfile',
    transactions: 'overview.row.viewCharge',
    bookings: 'overview.row.viewBooking',
    messages: 'overview.row.viewMessage',
    offers: 'overview.row.openOffer',
    blog: 'overview.row.openPost',
  }[kind])
  return (
    <div className="dash-overview-summary dash-overview-list-widget">
      <div className="dash-overview-summary-head">
        <div className="dash-overview-summary-head-main">
          <span className="dash-widget-heading-icon" aria-hidden="true">
            {image
              ? <img className="dash-summary-head-image" src={image} alt="" />
              : <Icon size={18} />}
          </span>
          {/* Título y recuento apilados: antes el recuento iba al lado en unas
              tarjetas y debajo en otras, y la fila de encabezados no cuadraba
              entre widgets vecinos. */}
          <span className="dash-summary-head-copy">
            {/* El punto va sólo en las filas, no aquí. Uno en el título decía
                "hay algo nuevo" sin decir qué, y obligaba a recorrer la lista
                para encontrarlo — que es lo que ya hacen los de las filas. */}
            <span className="dash-summary-title-row">
              <h3 className="dash-card-title">{title}</h3>
            </span>
            <span className="dash-card-meta">{count} {unit}</span>
          </span>
        </div>
        <button type="button" onClick={onAction} className="dash-secondary-btn dash-summary-action">
          {remaining > 0 ? t('overview.seeNMore', { n: remaining }) : action} <IconArrowUpRight size={13} />
        </button>
      </div>
      <div className="dash-summary-card-panel dash-contained-list-panel" style={{ display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
        <div className="dash-summary-card-items" ref={listRef}>
          {rows.length === 0 ? (
            /* En su caja, como las filas que ocuparían su sitio: un renglón
               de texto suelto sobre el fondo del widget parece que la lista no
               terminó de cargar. */
            <div className="dash-widget-empty">
              <span>{t('overview.empty')}</span>
              {emptyActionLabel ? (
                <button type="button" className="dash-widget-empty-link" onClick={onEmptyAction ?? onAction}>
                  {emptyActionLabel}
                </button>
              ) : null}
            </div>
          ) : visibleRows.map((row) => (
            <article
              className={`dash-summary-item-row${row.onClick ? ' is-clickable' : ''}`}
              key={row.key}
              /*
               * La fila entera abre, no sólo el título y la miniatura.
               *
               * Antes eran los dos únicos puntos vivos, así que pulsar el
               * subtítulo, la pastilla, la fecha o el aire entre ellos no hacía
               * nada: parecía que el clic fallaba a ratos cuando lo que fallaba
               * era la puntería que exigía la fila.
               *
               * Se ignora lo que nazca en algo interactivo —el menú, un enlace,
               * el propio título— para no disparar dos veces ni robarle el clic
               * al menú de tres puntos.
               */
              {...(row.onClick ? {
                onClick: (event: React.MouseEvent<HTMLElement>) => {
                  if ((event.target as HTMLElement).closest('button, a, input, select, textarea')) return
                  row.onClick?.()
                },
              } : {})}
            >
            <button className={`dash-summary-item-thumb${row.thumbnailUrl ? '' : ' has-glass-list-icon'}`} type="button" onClick={row.onClick} aria-label={translate('overview.openItem', { name: row.label })}>
              {row.thumbnailUrl ? <img src={row.thumbnailUrl} alt="" loading="lazy" /> : (
                <span className={`dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon ${row.isHome ? 'is-home' : 'is-fog'}`} aria-hidden="true">
                  {row.thumbIcon ? <row.thumbIcon size={13} /> : <Icon size={13} />}
                </span>
              )}
            </button>
            <span className={`dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon dash-summary-quarter-list-icon ${row.isHome ? 'is-home' : 'is-fog'}`} aria-hidden="true">
              {row.thumbIcon ? <row.thumbIcon size={13} /> : <Icon size={13} />}
            </span>
            <span className="dash-summary-item-copy">
              <span className="dash-summary-item-heading">
                <button className="dash-summary-item-title" type="button" onClick={row.onClick}>{row.label}</button>
                {row.category ? (
                  <span className="dash-summary-item-kind-chip" title={row.category.label}>
                    {row.category.icon}
                    <span>{row.category.label}</span>
                  </span>
                ) : null}
                {row.unread ? <span className="dash-unread-dot dash-unread-dot--row" title={t('overview.new')} aria-label={t('overview.new')} /> : null}
                {row.itemCount !== undefined ? (
                  /* Sólo la cifra: "elementos" se repite en cada fila y es lo
                     primero que le roba el ancho al nombre. El significado se
                     mantiene para quien no ve la columna. */
                  <span className="dash-summary-item-count" title={`${row.itemCount} ${row.itemCount === 1 ? 'elemento' : 'elementos'}`}>
                    <span aria-hidden="true">{row.itemCount}</span>
                    <span className="dash-sr-only">{row.itemCount} {row.itemCount === 1 ? 'elemento' : 'elementos'}</span>
                  </span>
                ) : null}
                {row.amount ? (
                  <span className={`dash-summary-item-amount${row.amountAccent ? ' is-accent' : ''}${row.amountNeutral ? ' is-neutral' : ''}${row.amountMuted ? ' is-muted' : ''}${row.amountAlert ? ' is-alert' : ''}`}>{row.amount}</span>
                ) : null}
                {row.statusLabel ? (
                  <span className={`dash-summary-item-chip dash-summary-item-status-chip${row.status ? ` is-${row.status}` : ''}`}>
                    {row.status === 'pending' ? <VelticaMascot outline size={14} /> : null}
                    {row.statusLabel}
                  </span>
                ) : null}
              </span>
              <span className="dash-summary-item-link-row">
                {row.href ? (
                  <a className="dash-summary-item-link" href={row.href} target="_blank" rel="noreferrer">{row.sub}</a>
                ) : row.sub ? (
                  <span className={row.subChip ? `dash-summary-item-chip${row.status ? ` is-${row.status}` : ''}` : 'dash-summary-item-link is-disabled'}>
                    {row.subIcon ? <span className="dash-summary-item-sub-icon" aria-hidden="true">{row.subIcon}</span> : null}
                    {row.subChip && row.status === 'pending' ? <VelticaMascot outline size={14} /> : null}
                    {row.sub}
                  </span>
                ) : null}
                {row.href ? <CopyLinkButton value={row.href} /> : null}
              </span>
              {/* Fuera de la línea del comprador y no dentro: en la rejilla de
                  Transacciones esto es su propia celda, la de abajo a la
                  derecha, justo debajo del importe. */}
              {row.subEnd ? <span className="dash-summary-item-sub-end">{row.subEnd}</span> : null}
            </span>
            <SummaryRowMenu row={row} primaryLabel={rowActionLabel} />
            </article>
          ))}
        </div>
      </div>
      {/* En 1/4 se usa el botón secundario real fuera del panel del listado. */}
      <button
        className="dash-secondary-btn dash-summary-more-button"
        type="button"
        onClick={onAction}
      >
        {remaining > 0 ? t('overview.seeNMore', { n: remaining }) : action}
        <IconArrowUpRight size={12} />
      </button>
    </div>
  )
}

/** The "Resumen" traffic block (stat cards + compact chart). Fed real stats for
 *  paid plans, and a fake dataset (blurred behind the upsell) for free. */
function AnalyticsSummarySection({ stats, loading, onNavigate, empty }: {
  stats: ProjectAnalytics | null
  loading: boolean
  onNavigate: (section: WorkspaceSection) => void
  /** Sin visitas todavía: el encabezado de siempre y, debajo, el vacío de los demás. */
  empty?: boolean | undefined
}) {
  const { t } = useI18n()
  const topPage = stats?.topPages[0]

  return (
    <section className="dash-overview-summary">
      <div className="dash-overview-summary-head">
        <div className="dash-overview-summary-head-main">
          <span className="dash-widget-heading-icon" aria-hidden="true">
            <IconChartBar size={18} />
          </span>
          <h2 className="dash-card-title">{t('overview.summary.title')}</h2>
          <span className="dash-card-meta" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('overview.last30')}</span>
        </div>
        <button type="button" onClick={() => onNavigate('analytics')} className="dash-secondary-btn dash-overview-analytics-action" style={{ flexShrink: 0 }}>
          {t('overview.viewAnalytics')} <IconArrowUpRight size={13} />
        </button>
      </div>
      {empty ? <EmptyWidgetPanel /> : <>
      <div className="dash-overview-analytics-stats dash-auto-grid is-compact">
        <StatCard loading={loading} label={t('overview.stat.views')} value={stats?.totals.views.toLocaleString() ?? ''} icon={IconClick} change={stats ? trend(stats.totals.views, stats.totals.viewsPrev) : null} />
        <StatCard loading={loading} label={t('overview.stat.uniques')} value={stats?.totals.uniques.toLocaleString() ?? ''} icon={IconUsers} change={stats ? trend(stats.totals.uniques, stats.totals.uniquesPrev) : null} />
        <StatCard loading={loading} label={t('overview.stat.countries')} value={stats?.countriesCount.toLocaleString() ?? ''} icon={IconWorld} />
        <StatCard loading={loading} label={t('overview.stat.topPage')} value={topPage ? pathLabel(topPage.path) : '—'} icon={IconFileText} />
      </div>
      <div className="dash-overview-analytics-chart-panel" style={{ ...innerPanel, padding: '32px 20px 24px' }}>
        {loading || !stats ? (
          <div className="sk dash-overview-chart-skeleton" />
        ) : (
          <div className="dash-loaded-content-in dash-overview-chart-fill">
            <TrafficChart
              series={stats.series}
              comparison={stats.comparison?.series ?? []}
              comparisonLabel={stats.comparison ? t('overview.previousPeriod') : null}
              granularity={stats.granularity}
              timezone={stats.timezone}
              compact
            />
          </div>
        )}
      </div>
      </>}
    </section>
  )
}

/**
 * Un widget sin datos todavía: el mismo vacío que las listas de Inicio
 * («Nada por aquí todavía.» en su caja), bajo su encabezado de siempre.
 */
function EmptyWidgetPanel() {
  const { t } = useI18n()
  return (
    <div className="dash-summary-card-panel dash-contained-list-panel">
      <div className="dash-summary-card-items">
        <div className="dash-widget-empty"><span>{t('overview.empty')}</span></div>
      </div>
    </div>
  )
}

/**
 * El gemelo del bloque de tráfico, pero del dinero: mismas cuatro tarjetas,
 * misma gráfica compacta, mismo encabezado con su enlace a la sección entera.
 *
 * Existe por lo mismo que existe el de visitas: la pregunta de la mañana —«¿cómo
 * voy?»— no debería costar entrar en una sección. La cifra que lleva la línea es
 * la de ingresos, que es lo que se mira primero; las ventas viajan en el tooltip
 * como los visitantes únicos en el de tráfico.
 */
function SalesSummarySection({ stats, loading, onNavigate, empty }: {
  stats: SalesAnalytics | null
  loading: boolean
  onNavigate: (section: WorkspaceSection) => void
  /** Sin ventas todavía: el encabezado de siempre y, debajo, el vacío de los demás. */
  empty?: boolean | undefined
}) {
  const { t } = useI18n()
  const { currency: projectCurrency } = useRegion()
  // Sin ventas todavía no hay moneda que deducir: se usa en la que cobra.
  const currency = stats?.currency ?? projectCurrency
  const cash = (amount: number) => formatMoney(amount, currency)
  /* La moneda va en pequeño: «USD 2,226.00» a tamaño de titular no cabía junto
     a la insignia de tendencia y se cortaba justo por los pesos. */
  const cashMetric = (amount: number) => <MoneyMetric amount={amount} currency={currency} />
  const totals = stats?.totals
  return (
    <section className="dash-overview-summary">
      <div className="dash-overview-summary-head">
        <div className="dash-overview-summary-head-main">
          <span className="dash-widget-heading-icon" aria-hidden="true">
            <IconCoins size={18} />
          </span>
          <h2 className="dash-card-title">{t('overview.sales.title')}</h2>
          <span className="dash-card-meta" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('overview.last30')}</span>
        </div>
        <button type="button" onClick={() => onNavigate('salesAnalytics')} className="dash-secondary-btn dash-overview-analytics-action" style={{ flexShrink: 0 }}>
          {t('overview.viewSalesAnalytics')} <IconArrowUpRight size={13} />
        </button>
      </div>
      {empty ? <EmptyWidgetPanel /> : <>
      <div className="dash-overview-analytics-stats dash-auto-grid is-compact">
        <StatCard loading={loading} label={t('overview.stat.revenue')} value={cashMetric(totals?.revenue ?? 0)} icon={IconCoins} change={totals ? trend(totals.revenue, totals.revenuePrev) : null} />
        <StatCard loading={loading} label={t('overview.stat.orders')} value={formatNumber((totals?.orders ?? 0))} icon={IconShoppingCart} change={totals ? trend(totals.orders, totals.ordersPrev) : null} />
        <StatCard loading={loading} label={t('overview.stat.aov')} value={cashMetric(totals?.aov ?? 0)} icon={IconReceipt} change={totals ? trend(totals.aov, totals.aovPrev) : null} />
        <StatCard loading={loading} label={t('overview.stat.customers')} value={formatNumber((totals?.customers ?? 0))} icon={IconUsers} change={totals ? trend(totals.customers, totals.customersPrev) : null} />
      </div>
      <div className="dash-overview-analytics-chart-panel" style={{ ...innerPanel, padding: '32px 20px 24px' }}>
        {loading || !stats ? (
          <div className="sk dash-overview-chart-skeleton" />
        ) : (
          <div className="dash-loaded-content-in dash-overview-chart-fill">
            <MetricChart
              series={stats.series.map((point) => ({
                day: point.day,
                ...(point.hour === undefined ? {} : { hour: point.hour }),
                primary: point.revenue,
                secondary: point.orders,
              }))}
              comparison={(stats.comparison?.series ?? []).map((point) => ({
                day: point.day,
                primary: point.revenue,
                secondary: point.orders,
              }))}
              comparisonLabel={stats.comparison ? t('overview.previousPeriod') : null}
              granularity={stats.granularity}
              timezone={stats.timezone}
              title={t('overview.chart.revenue')}
              primaryLabel={t('overview.revenue')}
              secondaryLabel={t('overview.sales')}
              formatPrimary={cash}
              formatAxis={(value) => formatMoney(value, currency, { compact: true })}
              compact
            />
          </div>
        )}
      </div>
      </>}
    </section>
  )
}

const DASHBOARD_ORDER = ['project-status', 'pages', 'offers', 'blog', 'bookings', 'cms', 'contacts', 'forms', 'toShip', 'orders', 'payouts', 'salesSummary', 'analytics', 'plan-usage', 'domain', 'notes']
const dashboardRank = (id: string) => {
  const at = DASHBOARD_ORDER.indexOf(id)
  return at < 0 ? DASHBOARD_ORDER.length - 1 : at
}

export function OverviewSection({
  projectId, token, arrivedBySwitch = false, onEnterAcceptedProject, userName, currentUser, projectName, projectNotes = '', projectRole = 'owner', publishedSubdomain, updatedAt, publishedAt, projectLoading = false, plan, pages, collections, onNavigate, onOpenEditor, onOpenEditorToPublish, onOpenPageSettings, onOpenStats, onPreviewPage, onOpenCms, onOpenPlans, onOpenContact, onOpenOffer, onCreateOffer, onCreateBooking, onOpenService, onOpenSubmission,
}: {
  projectId: string
  token: string
  /**
   * Si a esta pantalla se llegó cambiando de proyecto y no de cualquier otra
   * forma. Lo sabe quien puso la cortina del cambio, dos capas más arriba.
   */
  arrivedBySwitch?: boolean
  /**
   * Entrar a un proyecto recién aceptado, con la cortina del cambio puesta.
   *
   * Sube hasta `Home` por el mismo camino que `arrivedBySwitch` baja: quien
   * tiene la cortina y la lista de proyectos está dos capas más arriba, y aquí
   * sólo se sabe del proyecto que ya está abierto.
   */
  /* `| undefined` explícito por `exactOptionalPropertyTypes`: el taller lo pasa
     siempre, y ahí puede valer `undefined` cuando nadie se lo dio a él. */
  onEnterAcceptedProject?: ((projectId: string, projectName: string, role: ProjectRole) => void) | undefined
  userName: string
  currentUser?: { id: string; name: string; avatarUrl?: string | null } | undefined
  projectName: string
  /** Apuntes guardados del proyecto, para arrancar el widget Notas. */
  projectNotes?: string
  /** Quién mira: decide si el widget Notas deja escribir o sólo leer. */
  projectRole?: ProjectAccessRole
  publishedSubdomain: string | null
  updatedAt?: string | undefined
  publishedAt?: string | null | undefined
  /** True while the project itself is still loading (name/publish state unknown). */
  projectLoading?: boolean
  plan: BillingPlan
  pages: Page[]
  collections: CmsCollection[]
  onNavigate: (section: WorkspaceSection) => void
  onOpenEditor: (pageId?: string) => void
  /** Al editor, pero con el globo que señala Publicar encendido. */
  onOpenEditorToPublish?: () => void
  /** Las tres siguientes son las de la sección Páginas: el widget ofrece lo mismo. */
  onOpenPageSettings?: ((pageId: string) => void) | undefined
  onOpenStats?: ((pageId: string) => void) | undefined
  onPreviewPage?: ((pageId: string) => void) | undefined
  onOpenCms: (collectionId: string) => void
  onOpenPlans: () => void
  /** Abre la ficha de un contacto concreto, no sólo la sección. */
  onOpenContact?: ((contactId: string) => void) | undefined
  /** Abre una oferta en su ficha. Sin ella, el widget lleva al catálogo. */
  onOpenOffer?: ((offerId: string) => void) | undefined
  /** Ir a Ofertas con el selector de oferta nueva abierto. */
  onCreateOffer?: (() => void) | undefined
  /** Ir al calendario con el alta de una cita abierta. */
  onCreateBooking?: (() => void) | undefined
  /** Abrir el servicio de una cita, desde su ficha. */
  onOpenService?: ((offerId: string) => void) | undefined
  /** Abre un mensaje concreto, no sólo la bandeja. */
  onOpenSubmission?: ((submissionId: string) => void) | undefined
}) {
  const { t } = useI18n()
  // Analytics is a paid feature. Free users NEVER fetch real data — they see a
  // blurred, fake dataset behind the Pro upsell (so DevTools shows no real numbers).
  const isPaid = plan !== 'free'
  /* Qué puede leer quien mira, según su rol EN ESTE PROYECTO. Antes todo esto
     colgaba de `isAdmin`, que es ser personal de véltica: el dueño de un sitio
     abría Inicio y no veía ni sus mensajes ni sus ventas. */
  const canForms = can(projectRole, 'forms.read')
  const canContacts = can(projectRole, 'contacts.read')
  const canBookings = can(projectRole, 'bookings.read')
  const canCommerce = can(projectRole, 'commerce.read')
  const [widgetsEditing, setWidgetsEditing] = useState(false)
  /* Con la charla empezada, el saludo grande se recoge: la conversación ya
     lleva a la mascota en cada respuesta, y repetirla en grande encima empuja
     todo hacia abajo. Vuelve con «Empezar de nuevo». */
  const chatting = useAssistant().messages.length > 0 && !widgetsEditing
  /* Las entradas del blog, para su widget (ver más abajo). */
  /* Con lo de la última visita mientras llega lo nuevo (ver `overviewSnapshot`). */
  const [blog, setBlog] = useState<BlogOverview | null>(() => readSnapshot<BlogOverview>(projectId, 'blog') ?? null)
  const [dismissedSetupSteps, setDismissedSetupSteps] = useState<SetupStepDismissals>(() => readSetupStepDismissals(projectId))
  /* Lo que se supo la última vez, leído antes del primer pintado: si se leyera
     en un efecto, el esqueleto ya se habría dibujado un fotograma. */
  const [setupHadPending, setSetupHadPending] = useState(() => readSetupHadPending(projectId))
  const [range] = useState<AnalyticsRange>('30d')
  const [stats, setStats] = useState<ProjectAnalytics | null>(() => (isPaid ? getCachedAnalytics(projectId, range) ?? null : null))
  const [loading, setLoading] = useState(() => isPaid && !getCachedAnalytics(projectId, range))
  const [revealAnalytics, setRevealAnalytics] = useState(false)
  // Don't animate the reveal when we already had cached data (no skeleton was shown).
  const analyticsWasLoadingRef = useRef(isPaid && !getCachedAnalytics(projectId, range))

  /* Los cierres pertenecen al proyecto donde se hicieron. Al cambiar de sitio
     se cargan los suyos, sin ocultar por accidente un paso con la preferencia
     de otro proyecto. */
  useEffect(() => {
    setDismissedSetupSteps(readSetupStepDismissals(projectId))
    setSetupHadPending(readSetupHadPending(projectId))
  }, [projectId])

  /* Igual que la tarjeta de mejora del menú: no hace falta recargar. Cuando
     vence el primer cierre, se purga y la tarjeta reaparece; si quedan otros
     cierres más recientes, la siguiente ejecución se programa desde el nuevo
     estado. */
  useEffect(() => {
    const dismissedAt = Object.values(dismissedSetupSteps)
    if (dismissedAt.length === 0) return
    const remaining = Math.min(...dismissedAt.map((value) => SETUP_STEP_DISMISS_MS - (Date.now() - value)))
    const timer = window.setTimeout(() => {
      setDismissedSetupSteps(readSetupStepDismissals(projectId))
    }, Math.max(0, remaining))
    return () => window.clearTimeout(timer)
  }, [dismissedSetupSteps, projectId])

  useEffect(() => {
    if (!isPaid) { setLoading(false); return }
    let cancelled = false
    /* El tramo anterior SÍ se pide, y se usa: de él salen las flechas de
       tendencia de las tarjetas —`viewsPrev`, `uniquesPrev`— y la línea de
       comparación de la gráfica (`stats.comparison`). Aquí ponía justo lo
       contrario, «el resumen no compara», que es la clase de comentario que
       hace que alguien quite el parámetro para ahorrar trabajo y se lleve por
       delante las flechas sin enterarse. */
    const analyticsQuery = { range, compare: 'previous' as const }
    const cacheKey = analyticsQueryKey(analyticsQuery)
    const cached = getCachedAnalytics(projectId, cacheKey)
    // Show cached data instantly; only show the skeleton on a true first load.
    if (cached) { setStats(cached); setLoading(false) } else { setLoading(true) }
    void (async () => {
      try {
        const data = await getProjectAnalytics(token, projectId, analyticsQuery)
        if (cancelled) return
        setCachedAnalytics(projectId, cacheKey, data)
        setStats(data)
      } catch {
        if (!cancelled && !cached) setStats(null)
      }
      if (!cancelled) setLoading(false)
    })()
    return () => { cancelled = true }
  }, [projectId, token, range, isPaid])

  useEffect(() => {
    const wasLoading = analyticsWasLoadingRef.current
    analyticsWasLoadingRef.current = loading
    if (loading) {
      setRevealAnalytics(false)
      return
    }
    if (!wasLoading) return
    setRevealAnalytics(true)
    const timer = window.setTimeout(() => setRevealAnalytics(false), 700)
    return () => window.clearTimeout(timer)
  }, [loading])

  // The project's canonical custom domain (active + primary), if any — it becomes
  // the site's main URL everywhere the subdomain used to be shown.
  /* Los dominios y el uso, con lo de la última visita mientras llega lo nuevo. */
  const [domains, setDomains] = useState<CustomDomain[]>(() => readSnapshot<CustomDomain[]>(projectId, 'domains') ?? [])
  const [primaryDomain, setPrimaryDomain] = useState<string | null>(() => domains.find((x) => x.isPrimary && x.status === 'active')?.hostname ?? null)
  const [usage, setUsage] = useState<AccountUsage | null>(() => readSnapshot<AccountUsage>(projectId, 'usage') ?? null)
  useEffect(() => {
    let alive = true
    listProjectDomains(token, projectId)
      .then((d) => {
        if (!alive) return
        setDomains(d.domains)
        writeSnapshot(projectId, 'domains', d.domains)
        setPrimaryDomain(d.domains.find((x) => x.isPrimary && x.status === 'active')?.hostname ?? null)
      })
      .catch(() => {})
    // El uso es de la cuenta, no del proyecto: se pide una vez por sesión de vista.
    getAccountUsage(token).then((data) => { if (alive) { setUsage(data); writeSnapshot(projectId, 'usage', data) } }).catch(() => {})
    return () => { alive = false }
  }, [token, projectId])

  const isPublished = Boolean(publishedSubdomain)

  /**
   * Lo que la mascota puede contar hoy porque es cierto.
   *
   * Se arman aquí y no dentro del globo porque aquí están los datos y aquí se
   * sabe qué significan: allí un 24% es un número suelto. Cada frase sale sólo
   * si hay dato que la sostenga — y ninguna inventa una tendencia con cifras de
   * un día, que es como se acaban diciendo cosas falsas con datos verdaderos.
   */
  /*
   * Lo que la mascota dice cuando hay algo esperando arriba.
   *
   * Los cuatro avisos del inicio se piden solos y se pintan solos, así que esta
   * pantalla no sabría que existen; lo cuentan por `lib/pendingNotices`. Con
   * alguno vivo, la mascota levanta la vista hacia él y lo nombra — mirar sin
   * decir nada se lee como un tic, y decirlo sin mirar deja la frase flotando
   * sobre un recuadro cualquiera. Las dos cosas juntas señalan.
   *
   * Sin acción a propósito: el aviso de arriba ya tiene sus botones, y repetir
   * aquí el mismo haría dudar de si son dos cosas distintas.
   */
  const notices = usePendingNotices()
  const waiting = topNotice(notices)
  const waitingLine = useMemo<MascotFact | undefined>(() => {
    if (!waiting) return undefined
    const count = notices[waiting]
    const many = (one: string, more: string) => (count === 1 ? one : more)
    const text = waiting === 'handover'
      ? many(t('overview.handoverOne'), t('overview.handoverMany', { count }))
      : waiting === 'transfer'
        ? many(
            t('overview.transferInvite'),
            t('overview.transferInviteMany', { count }),
          )
        : many(
            t('overview.teamInvite'),
            t('overview.teamInviteMany', { count }),
          )
    return { text }
    /* Y `t`, que es de donde sale el texto: sin ella el globo seguía hablando el
       idioma anterior después de cambiarlo. */
  }, [waiting, notices, t])

  /**
   * Y el encabezado del globo, en vez del saludo de la hora.
   *
   * Corto y sin nombre propio: es un titular, no una conversación, y el nombre
   * de quien mira no aporta nada a «te falta algo». Lo que sigue debajo es la
   * frase de arriba, que cuenta cuál.
   */
  const waitingGreeting = useMemo<string | undefined>(() => {
    if (!waiting) return undefined
    const count = notices[waiting]
    if (waiting === 'handover') {
      return count === 1 ? t('overview.waiting.stepOne') : t('overview.waiting.stepMany', { count })
    }
    if (waiting === 'transfer') return t(count === 1 ? 'overview.waiting.transferOne' : 'overview.waiting.transferMany')
    return count === 1 ? t('overview.waiting.inviteOne') : t('overview.waiting.inviteMany', { count })
    /* Igual que en `mascotFacts`: el saludo se rehace al cambiar de idioma, y
       rehacerlo no cuesta nada porque sólo lee lo que ya está en memoria. */
  }, [waiting, notices, t])

  const mascotFacts = useMemo<MascotFact[]>(() => {
    const lines: MascotFact[] = []
    /* Casi todos llevan su botón: un hecho que señala algo y no dice dónde se
       atiende deja al cliente buscando por el menú lo que la frase acaba de
       nombrar. Los que no lo llevan es porque no hay nada que hacer con ellos. */
    const analytics = { label: t('overview.fact.seeAnalytics'), run: () => onNavigate('analytics') }
    const totals = stats?.totals
    if (totals && totals.views > 0) {
      lines.push({
        text: t(totals.views === 1 ? 'overview.fact.viewsOne' : 'overview.fact.views', { count: formatNumber(totals.views) }),
        action: analytics,
      })
      /* Sólo con un periodo anterior que tuviera tráfico: un salto desde cero es
         siempre «+100%» y no dice nada. */
      if (totals.viewsPrev > 0) {
        const change = Math.round(((totals.views - totals.viewsPrev) / totals.viewsPrev) * 100)
        if (change >= 5) lines.push({ text: t('overview.fact.viewsUp', { percent: change }), action: analytics })
        else if (change <= -5) lines.push({ text: t('overview.fact.viewsDown', { percent: Math.abs(change) }), action: analytics })
      }
    }
    const top = stats?.topPages[0]
    if (top) lines.push({ text: t('overview.fact.topPage', { page: pathLabel(top.path) }), action: analytics })
    if (!isPublished) {
      lines.push({
        text: t('overview.fact.notPublished'),
        /* Al editor y no a Dominio: publicar se hace desde ahí, y mandarlo a la
           pantalla de la dirección sería enseñarle dónde vivirá el sitio en vez
           de dónde se pone en línea. */
        action: { label: t('overview.fact.openEditor'), run: () => onOpenEditor() },
      })
    }
    if (collections.length > 0) {
      lines.push({
        text: t(collections.length === 1 ? 'overview.fact.collectionsOne' : 'overview.fact.collections', { count: collections.length }),
        action: { label: t('overview.fact.goContent'), run: () => onNavigate('cms') },
      })
    }
    if (pages.length === 1) {
      lines.push({
        text: t('overview.fact.onePage'),
        action: { label: t('overview.fact.goPages'), run: () => onNavigate('pages') },
      })
    }
    return lines
    /* `t` entra en las dependencias a propósito: estas frases se pintan tal cual
       y tienen que rehacerse al cambiar de idioma. Aquí no cuesta nada —el
       cálculo es sobre datos que ya están en memoria—, al contrario que en los
       efectos que piden algo al servidor. */
  }, [stats, isPublished, collections.length, pages.length, onNavigate, onOpenEditor, t])

  const publishedRelative = relativeProjectTime(publishedAt)
  const updatedRelative = relativeProjectTime(updatedAt)
  const siteUrl = primaryDomain
    ? `https://${primaryDomain}`
    : publishedSubdomain ? publicSiteUrl(publishedSubdomain) : null
  // Show the name/publish-state skeleton until the project has actually loaded.
  const showHeaderSkeleton = projectLoading && !projectName

  // For free users, drive the Resumen with fake data (blurred behind the upsell).
  const fake = useMemo(() => fakeAnalytics(range), [range])
  const displayStats = isPaid ? stats : fake
  const displayLoading = isPaid ? loading : false
  const hasTraffic = Boolean(displayStats && displayStats.totals.views > 0)

  const sitePages = dashboardSitePages(pages)
  const homePage = sitePages.find((p) => p.isHome)
  /**
   * Un solo fetch alimenta las dos tarjetas: los contactos se derivan de los
   * envíos con la misma función que usa su sección, así que las cifras de aquí
   * y las de allí no pueden discrepar.
   */
  const [submissions, setSubmissions] = useState<FormSubmission[]>([])
  const [newContacts, setNewContacts] = useState<ContactRecord[]>([])
  const [upcoming, setUpcoming] = useState<Booking[]>([])
  /* La cuenta va por CITAS y no por entradas: una clase de seis son seis citas
     en la agenda aunque ocupen un solo renglón de la lista. */
  const upcomingCount = upcoming.filter((booking) => booking.status === 'confirmed').length
  /** El total lo dice el servidor: `newContacts` sólo trae las que se enseñan. */
  const [contactsTotal, setContactsTotal] = useState(0)
  /* Publicadas, que es lo que cuenta el tope. `null` mientras no llega: con un
     cero de relleno, quien tiene tres vería «0 de 3» durante un instante — y ese
     instante es justo cuando se está mirando. */
  const [publishedOffers, setPublishedOffers] = useState<number | null>(null)
  /* Y el catálogo entero para su widget: la misma respuesta sirve a los dos. */
  const [catalog, setCatalog] = useState<Offer[]>([])
  /* Si ya se sabe cuántas ofertas hay, pase lo que pase con la petición. El paso
     «Crea tu primera oferta» sale de aquí: evaluarlo con el `[]` de arranque lo
     daba por pendiente y el bloque asomaba un segundo para irse al llegar el
     catálogo. Si la petición falla también cuenta como sabido —sin él, un rol
     que no lee ofertas dejaría los pasos en hueco para siempre—. */
  const [catalogSettled, setCatalogSettled] = useState(false)

  // Estas dos tarjetas enseñan doce filas, así que piden doce. Antes se traían
  // la bandeja y los contactos enteros para quedarse con la punta de la lista.
  useEffect(() => {
    if (!projectId || !token) return
    let alive = true
    /*
     * Las reservas se piden por RANGO y no por página, porque su ruta va por
     * fechas: de hoy a dentro de dos meses, que es lo que puede haber agendado.
     * Lo pasado no entra —un widget de «lo que viene» que empieza por lo que ya
     * pasó no sirve para lo que se abre el panel por la mañana—.
     */
    const desde = new Date().toISOString().slice(0, 10)
    const hasta = new Date(Date.now() + 60 * 86_400_000).toISOString().slice(0, 10)
    void Promise.all([
      canForms ? listSubmissions(projectId, token, { pageSize: WIDGET_ROWS }).catch(() => null) : null,
      canContacts ? listContacts(projectId, token, { pageSize: WIDGET_ROWS, sort: 'firstDesc' }).catch(() => null) : null,
      canBookings ? listBookings(projectId, { from: desde, to: hasta }, token).catch(() => null) : null,
      getBlog(projectId, token).catch(() => null),
    ]).then(([inbox, contacts, bookings, blogData]) => {
      if (!alive) return
      if (inbox) setSubmissions(inbox.data)
      if (contacts) { setNewContacts(contacts.data); setContactsTotal(contacts.total) }
      if (bookings) setUpcoming(bookings)
      if (blogData) { setBlog(blogData); writeSnapshot(projectId, 'blog', blogData) }
    })
    return () => { alive = false }
  }, [canForms, canContacts, canBookings, projectId, token])

  /*
   * El catálogo, aparte y sin candado de administrador.
   *
   * Iba dentro de la petición de arriba, que sólo corre para el personal de la
   * casa —`isAdmin` es el permiso de la PLATAFORMA, no el de dueño del
   * proyecto—, así que la barra de ofertas de «Uso del plan» no aparecía nunca
   * para quien la iba a mirar: su dueño. Aquí se pide siempre, y lo que decide
   * si se enseña es que la respuesta llegue.
   *
   * Si el rol no lo puede leer, la petición falla y se calla: sin recuento no
   * hay barra, que es mejor que una barra en cero fingiendo un catálogo vacío.
   */
  useEffect(() => {
    if (!projectId || !token) return
    let alive = true
    setCatalogSettled(false)
    void listOffers(projectId, token)
      .then((rows) => {
        if (!alive) return
        setCatalog(rows)
        setPublishedOffers(rows.filter((offer) => offer.status === 'active').length)
      })
      .catch(() => {})
      .finally(() => { if (alive) setCatalogSettled(true) })
    return () => { alive = false }
  }, [projectId, token])

  /**
   * Las ventas del proyecto, para el widget de Transacciones.
   *
   * Sólo se piden en los planes de pago, con el mismo trato que las analíticas:
   * quien no lo tiene ve cobros inventados detrás del candado, así que por su
   * navegador no pasa ni una venta real. La lista viene ya del cobro más nuevo
   * al más viejo, así que la tarjeta se queda con la punta y no ordena nada.
   */
  const [orders, setOrders] = useState<Order[]>([])
  const [openOrder, setOpenOrder] = useState<Order | null>(null)
  /* La cita abierta, igual que la venta: la ficha se consulta y se cierra sin
     salir del inicio. Se guarda la cita entera y no su id porque ya la tenemos
     cargada —pedirla otra vez al servidor sería un viaje para traer lo mismo—. */
  const [openBooking, setOpenBooking] = useState<Booking | null>(null)
  /* La clase abierta, como ENTRADA y no como lista de citas: es lo que espera
     `GroupBookingDialog`, que ya sabe pintar un grupo con su desglose. */
  const [openGroup, setOpenGroup] = useState<Extract<BookingEntry, { kind: 'group' }> | null>(null)
  /* Las portadas de los servicios, sólo para la ficha del grupo. Se piden con
     las reservas y no al abrirla: una consulta por cada vez que se abre un grupo
     sería un viaje para traer una imagen. */
  /* Del catálogo que ya se pidió: era un estado propio alimentado por la misma
     respuesta, y dos copias de lo mismo es una que se queda vieja. */
  const serviceInfo = useMemo(
    () => new Map(catalog.map((offer) => [offer.id, { image: offer.images[0] ?? '', capacity: offer.booking.capacity }] as const)),
    [catalog],
  )
  useEffect(() => {
    if (!canCommerce || !projectId || !token) return
    let alive = true
    listOrders(projectId, token)
      .then((rows) => { if (alive) setOrders(rows) })
      .catch(() => {})
    return () => { alive = false }
  }, [canCommerce, projectId, token])

  /**
   * El saldo en Stripe, para el widget de depósitos.
   *
   * Cifras y no el componente incrustado: aquí cabe una línea, y montar el
   * iframe de Stripe habría abierto su sesión en cada visita a Inicio para
   * enseñar dos números. El detalle entero está en su sección.
   *
   * `null` es «no hay cuenta conectada» —el servidor responde 409— y eso no es
   * un fallo: el widget sencillamente no se pinta. Sin plan tampoco se pide,
   * igual que las ventas: por ese navegador no pasa una cifra real.
   */
  const [balance, setBalance] = useState<StripeBalance | null>(null)
  useEffect(() => {
    if (!canCommerce || !projectId || !token) return
    let alive = true
    getStripeBalance(projectId, token)
      .then((data) => { if (alive) setBalance(data) })
      .catch(() => {})
    return () => { alive = false }
  }, [canCommerce, projectId, token])

  /**
   * Las analíticas de venta del mismo tramo que las de tráfico, para el widget
   * de Resumen de ventas.
   *
   * Consulta propia y no derivada de `orders`: sumar el historial aquí daría un
   * total de siempre, no de los últimos treinta días, y no habría con qué
   * comparar para la flecha de tendencia. Comparte la caché con su sección, así
   * que entrar a Ventas › Analíticas después de pasar por Inicio no vuelve a
   * pedirlo.
   */
  const salesQuery = useMemo(() => ({ range, compare: 'previous' as const }), [range])
  const salesKey = analyticsQueryKey(salesQuery)
  const [sales, setSales] = useState<SalesAnalytics | null>(() => getCachedSalesAnalytics(projectId, salesKey) ?? null)
  const [salesLoading, setSalesLoading] = useState(() => !getCachedSalesAnalytics(projectId, salesKey))
  useEffect(() => {
    if (!canCommerce || !isPaid || !projectId || !token) { setSalesLoading(false); return }
    let alive = true
    const cached = getCachedSalesAnalytics(projectId, salesKey)
    if (cached) { setSales(cached); setSalesLoading(false) } else { setSalesLoading(true) }
    getSalesAnalytics(token, projectId, salesQuery)
      .then((data) => {
        if (!alive) return
        setCachedSalesAnalytics(projectId, salesKey, data)
        setSales(data)
      })
      .catch(() => { if (alive && !cached) setSales(null) })
      .finally(() => { if (alive) setSalesLoading(false) })
    return () => { alive = false }
    // `salesKey` resume la consulta entera; `salesQuery` es un objeto nuevo en
    // cada dibujado y dispararía el efecto sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCommerce, isPaid, projectId, token, salesKey])

  /* Las ventas son las de verdad en todos los planes: el gratuito también
     vende, así que esconderle las suyas detrás de un candado era esconderle su
     propio dinero. Lo que sigue siendo de pago es la ANALÍTICA. */
  const displayOrders = orders
  const orderRows: SummaryRow[] = displayOrders.slice(0, WIDGET_ROWS).map((order) => {
    /* La disputa manda sobre el estado, igual que en la lista de Cobros: en
       esta celda sólo cabe la respuesta a «¿tengo que hacer algo con esto?», y
       mientras hay disputa esa respuesta es la disputa. Una venta puede estar
       reembolsada Y disputada —lo está la que hay ahora mismo— y decir sólo
       «Reembolsado» esconde justamente la mitad que corre prisa. */
    const disputed = disputeNeedsAction(order.disputeStatus)
    /* Respondida y esperando veredicto. No enciende la fila —no hay nada que
       hacer— pero tampoco la deja decir su precio: ese dinero todavía puede
       volverse atrás, y el widget es donde se mira cuánto se vendió. */
    const underReview = !disputed && disputeUnderReview(order.disputeStatus)
    return {
      key: order.id,
      label: order.offerTitle || t('overview.order.deletedOffer'),
      sub: order.buyerName || order.buyerEmail || t('overview.order.noBuyer'),
      subEnd: relativeDay(order.createdAt),
      /* Devuelta, la cápsula dice la palabra en vez de la cifra. Enseñar el
         importe contaría como cobrado un dinero que ya salió, y tacharlo obliga a
         leer un número para acabar descartándolo: lo que se necesita saber de esa
         fila es que se devolvió, y eso se lee antes escrito que deducido. */
      amount: disputed ? t('overview.order.disputed')
        : underReview ? t('overview.underReview')
        : order.status === 'paid' ? order.formatted : orderStatusLabel(order.status, t),
      /* Apagada la devuelta, resaltada la disputada: una es dinero que ya salió
         y la otra dinero que se va a ir si nadie la atiende. La que está en
         revisión va apagada con las primeras: aún no se ha ido, pero tampoco se
         puede contar. */
      amountMuted: !disputed && (underReview || order.status !== 'paid'),
      amountAlert: disputed,
      onClick: () => setOpenOrder(order),
    }
  })

  /* Los pedidos físicos que faltan por mandar: lo único de las ventas que pide
     hacer algo hoy. El más antiguo primero, que es el que lleva más esperando. */
  const toShipOrders = displayOrders.filter(awaitsShipping).reverse()
  const toShipRows: SummaryRow[] = toShipOrders.slice(0, WIDGET_ROWS).map((order) => ({
    key: order.id,
    label: order.offerTitle || t('overview.order.deletedOffer'),
    sub: order.shippingName || order.buyerName || order.buyerEmail || t('overview.order.noBuyer'),
    subEnd: relativeDay(order.createdAt),
    amount: t('orders.fulfillment.unfulfilled'),
    onClick: () => setOpenOrder(order),
  }))

  const recentSubmissions = submissions
  const seenContactIds = readDashboardSeenIds(projectId, 'contacts')
  const seenSubmissionIds = readDashboardSeenIds(projectId, 'forms')

  /*
   * Lo que viene, agrupado y en el molde de Transacciones.
   *
   * Como allí: el título es el SERVICIO y quien reserva va debajo. En una venta
   * la cifra es la mitad de la información y va a la derecha; en una cita esa
   * mitad es la hora, así que ocupa su sitio.
   *
   * Sin lo cancelado, y sin lo apartado tampoco: un `held` es un carrito a medio
   * pagar que caduca solo, y enseñarlo como cita agendada promete algo que puede
   * no ocurrir.
   */
  const bookingRows: SummaryRow[] = useMemo(() => {
    const vivas = upcoming
      .filter((booking) => booking.status === 'confirmed')
      .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
    return groupBookings(vivas).slice(0, WIDGET_ROWS).map((entry) => {
      const lead = entry.kind === 'group' ? entry.lead : entry.booking
      const cuando = new Date(lead.startsAt)
      /* En la zona de la cita, que viaja con ella: un negocio en Hermosillo que
         abra el panel desde Madrid vería todas sus citas movidas. */
      const hora = new Intl.DateTimeFormat(intlLocale(activeLanguage()), {
        timeZone: lead.timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).format(cuando)
      const dia = new Intl.DateTimeFormat(intlLocale(activeLanguage()), {
        timeZone: lead.timezone, weekday: 'short', day: 'numeric', month: 'short',
      }).format(cuando)
      return {
        key: entry.key,
        label: lead.offerTitle || 'Cita',
        /* Una clase no tiene «un cliente»: tiene seis. Nombrar al primero sería
           elegir a uno por orden de compra y esconder a los demás. */
        sub: entry.kind === 'group'
          ? peopleLabel(entry.members.length, t)
          : lead.customerName || lead.customerEmail || t('common.unnamed'),
        amount: hora,
        /* Ocupa el sitio del importe —en una cita la hora es la mitad de la
           información, como el precio en una venta— pero no su color: el verde de
           esa pastilla es de ingreso, y una hora ahí se lee como dinero entrado.
           Va en el acento, que es el color de «esto es lo tuyo». */
        amountAccent: true,
        /* Y «4 personas» como pastilla, igual que en la lista de Citas: es un
           dato del grupo, no el nombre de un cliente, y en texto plano se leía
           como si alguien se llamara así. */
        ...(entry.kind === 'group' ? { subChip: true } : {}),
        subEnd: dia,
        /* La ficha se abre AQUÍ, como la de una venta: nadie pidió cambiar de
           pantalla, sólo mirar la cita. Una clase sí sale al listado: la ficha es
           de UNA cita, y de un grupo no hay una que sea «la» cita —allí se
           despliega y se elige a quién abrir—. */
        onClick: () => { if (entry.kind === 'group') setOpenGroup(entry); else setOpenBooking(lead) },
      }
    })
  }, [upcoming, t])

  const contactRows = newContacts.map((contact) => ({
    key: contact.id,
    label: contact.name || contact.email || translate('overview.unnamedContact'),
    sub: contact.email && contact.name ? contact.email : relativeDay(contact.firstSeen),
    unread: !seenContactIds.has(contact.id),
    onClick: () => {
      markDashboardItemsSeen(projectId, 'contacts', [contact.id])
      return onOpenContact ? onOpenContact(contact.id) : onNavigate('contacts')
    },
  }))

  const submissionRows = recentSubmissions.map((submission) => ({
    key: submission.id,
    label: submission.name || submission.email || t('overview.unnamedSubmission'),
    sub: `${submission.formName} · ${relativeDay(submission.createdAt)}`,
    unread: !seenSubmissionIds.has(submission.id),
    onClick: () => {
      markDashboardItemsSeen(projectId, 'forms', [submission.id])
      return onOpenSubmission ? onOpenSubmission(submission.id) : onNavigate('forms')
    },
  }))

  /*
   * Las ofertas del widget: las últimas creadas, con su precio y su estado.
   *
   * El estado va como pastilla y no como texto porque es lo que decide si eso
   * está a la venta o sólo escrito — y es justo lo que se viene a comprobar de
   * un vistazo desde Inicio. El importe, al final, como en Transacciones.
   */
  /* El hueco de la barra de arriba. Se busca cada vez que cambia el modo
     porque sólo entonces importa, y es el mismo que usa la rejilla para su
     «Agregar widget». */
  const [widgetToolbarSlot, setWidgetToolbarSlot] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setWidgetToolbarSlot(document.getElementById(WIDGET_TOOLBAR_SLOT_ID))
  }, [widgetsEditing])

  const widgetEditToggle = (
    <button
      type="button"
      className={widgetsEditing ? 'dash-primary-btn dash-widget-edit-toggle is-editing' : 'dash-secondary-btn dash-widget-edit-toggle'}
      aria-pressed={widgetsEditing}
      onClick={() => setWidgetsEditing((current) => !current)}
    >
      {widgetsEditing ? <IconCheck size={14} /> : <IconSettings2 size={14} />}
      {t(widgetsEditing ? 'common.done' : 'overview.editWidgets')}
    </button>
  )

  const offerRows: SummaryRow[] = catalog.slice(0, 12).map((offer) => {
    const price = mainPrice(offer)
    const kind = kindOf(offer)
    const KindIcon = KIND_ICON[kind]
    return {
      key: offer.id,
      label: offer.title || t('common.untitled'),
      thumbIcon: KindIcon,
      sub: t(KIND_KEY[kind]),
      subIcon: <KindIcon size={13} aria-hidden="true" />,
      /* «Por completar» por delante de borrador: es lo que dice qué hacer con ella. */
      statusLabel: t(offer.setupPending ? 'aiPending.chip' : offer.status === 'active' ? 'offers.status.active' : 'offers.status.draft'),
      /* «pending» pinta la pastilla en ámbar con el icono de IA, como AiPendingChip. */
      status: offer.setupPending ? 'pending' : offer.status,
      ...(price ? { amount: formatMoney(price.unitAmount, price.currency), amountNeutral: true } : {}),
      thumbnailUrl: offer.images[0] ?? null,
      /* Una reserva se abre en Servicios programados; el resto, en su ficha del
         catálogo.
         Y a propósito distinto de lo que hace la LISTA de Cobros, donde una
         reserva se queda en el catálogo: allí ya estás en el catálogo y saltar
         de sección sería sacarte de donde estabas, mientras que desde Inicio no
         estás en ninguna de las dos y este widget es un atajo — lleva a donde
         se trabaja esa cosa, que para una agenda es su propia sección. */
      onClick: () => {
        if (kind === 'booking' && onOpenService) { onOpenService(offer.id); return }
        if (onOpenOffer) { onOpenOffer(offer.id); return }
        onNavigate('payments')
      },
    }
  })

  const pageRows = [
    ...(homePage ? [homePage] : []),
    ...sitePages.filter((p) => !p.isHome).slice(0, 11),
  ].map((p) => {
    const path = p.isHome ? '/' : `/${p.slug.replace(/^\/+/, '')}`
    return {
      key: p.id,
      label: p.name || t(p.isHome ? 'pages.home' : 'common.untitled'),
      sub: path,
      href: siteUrl ? `${siteUrl}${path === '/' ? '' : path}` : null,
      thumbnailUrl: p.thumbnailUrl,
      isHome: p.isHome,
      ...(p.isHome ? { thumbIcon: IconHome } : {}),
      onClick: () => onOpenEditor(p.id),
      // Las mismas seis que la sección Páginas, con sus mismos iconos: es la
      // misma página, y dos menús que ofrecen cosas distintas para lo mismo
      // obligan a recordar por dónde se entró.
      actions: [
        { key: 'editor', label: t('overview.openInEditor'), icon: <IconLayout size={14} />, onSelect: () => onOpenEditor(p.id) },
        { key: 'settings', label: t('common.settings'), icon: <IconSettings size={14} />, onSelect: () => onOpenPageSettings?.(p.id) },
        { key: 'stats', label: t('overview.stats'), icon: <IconChartBar size={14} />, onSelect: () => onOpenStats?.(p.id) },
        { key: 'preview', label: t('common.preview'), icon: <IconEye size={14} />, onSelect: () => onPreviewPage?.(p.id), separatorBefore: true },
        ...(siteUrl ? [
          /* Como dueño: el enlace firmado deja la barra de administración en el
             propio dominio del sitio, así que se llega a la página ya en modo
             administrador en vez de como un visitante cualquiera. */
          { key: 'live', label: t('overview.openPublished'), icon: <IconExternalLink size={14} />, onSelect: () => { void openPublishedAsOwner(token, projectId, `${siteUrl}${path === '/' ? '' : path}`) } },
          { key: 'copy', label: t('overview.copyLink'), icon: <IconLink size={14} />, onSelect: () => { void navigator.clipboard?.writeText(`${siteUrl}${path === '/' ? '' : path}`) } },
        ] : []),
      ],
    }
  })

  /* Las entradas, de la más reciente a la más vieja, con su estado. */
  const blogRows: SummaryRow[] = [...(blog?.posts ?? [])]
    .sort((a, b) => (b.publishedAt ?? b.updatedAt).localeCompare(a.publishedAt ?? a.updatedAt))
    .slice(0, 12)
    .map((post) => ({
      key: post.id,
      label: post.title || t('common.untitled'),
      sub: t(post.setupPending ? 'aiPending.chip' : `blog.state.${post.state}`),
      ...(post.setupPending ? { subChip: true, status: 'pending' } : {}),
      onClick: () => onNavigate('blog'),
    }))

  const collectionRows = collections.slice(0, 12).map((c) => {
    const collectionPage = pages.find((page) => page.cmsDynamic?.collection === c.id && page.cmsDynamic.kind === 'collection')
    const path = collectionPage ? `/${collectionPage.slug.replace(/^\/+/, '')}` : null
    return {
      key: c.id,
      label: c.name || t('cms.collection'),
      sub: path ?? t('overview.noPublishedLink'),
      itemCount: c.items?.length ?? 0,
      href: siteUrl && path ? `${siteUrl}${path}` : null,
      // En el resumen de CMS todas las colecciones comparten un icono neutro:
      // una captura diminuta parecía una página y hacía difícil leer la lista.
      thumbnailUrl: null,
      onClick: () => onOpenCms(c.id),
    }
  })

  /*
   * Lo que la IA dejó «por completar» al crear el sitio, en UN paso.
   *
   * Pueden ser doce cosas —tres de cada tipo—, y doce avisos sueltos abruman a
   * quien acaba de empezar. Aquí se agrupan («3 reservas · 2 servicios · 1
   * entrada del blog») y el botón abre la primera, en el orden en que dan
   * dinero: reservas, servicios, productos y, al final, el blog. Sólo sale
   * mientras quede alguna; al terminar la última, desaparece.
   */
  const pendingOffers = catalog.filter((offer) => offer.setupPending)
  const pendingByKind = {
    booking: pendingOffers.filter((offer) => kindOf(offer) === 'booking'),
    service: pendingOffers.filter((offer) => kindOf(offer) === 'service'),
    digital: pendingOffers.filter((offer) => kindOf(offer) === 'digital' || kindOf(offer) === 'course'),
  }
  const pendingPosts = (blog?.posts ?? []).filter((post) => post.setupPending)
  const pendingParts = [
    pendingByKind.booking.length ? t(pendingByKind.booking.length === 1 ? 'aiPending.n.booking' : 'aiPending.n.bookings', { count: pendingByKind.booking.length }) : '',
    pendingByKind.service.length ? t(pendingByKind.service.length === 1 ? 'aiPending.n.service' : 'aiPending.n.services', { count: pendingByKind.service.length }) : '',
    pendingByKind.digital.length ? t(pendingByKind.digital.length === 1 ? 'aiPending.n.digital' : 'aiPending.n.digitals', { count: pendingByKind.digital.length }) : '',
    pendingPosts.length ? t(pendingPosts.length === 1 ? 'aiPending.n.post' : 'aiPending.n.posts', { count: pendingPosts.length }) : '',
  ].filter(Boolean)
  const openFirstPending = () => {
    const booking = pendingByKind.booking[0]
    if (booking) { (onOpenService ?? onOpenOffer)?.(booking.id); return }
    const offer = pendingByKind.service[0] ?? pendingByKind.digital[0]
    if (offer) { onOpenOffer?.(offer.id); return }
    onNavigate('blog')
  }
  const completeStep = pendingParts.length ? [{
    key: 'ai-complete',
    icon: MascotIcon,
    label: t('aiPending.stepTitle'),
    description: t('aiPending.stepBody', { list: pendingParts.join(' · ') }),
    done: false,
    cta: t('aiPending.stepGo'),
    onAction: openFirstPending,
  }] : []

  const steps = [
    ...completeStep,
    /* El tono de cada paso NO es decorativo: es el que esa misma área ya tiene
       en el menú lateral y en el selector de la barra —Páginas azul, Contenido
       violeta, Editor verde—. Una paleta propia aquí hacía que la misma cosa
       tuviera un color en un sitio y otro tres píxeles más allá. */
    /* Los de «agregar» en su variante con el más —una página nueva, una colección
       nueva—: el paso no es visitar la sección, es crear algo dentro. */
    { key: 'pages', icon: IconBrowserPlus, label: t('onboard.pages'), description: t('onboard.pagesBody'), done: sitePages.length >= 2, cta: t('onboard.goPages'), onAction: () => onNavigate('pages') },
    /* Vender es lo que más le importa a quien arranca un sitio: la primera
       oferta —un servicio, un producto digital, una reserva— va antes que el
       CMS, que muchos sitios no necesitan. Lleva a Ofertas con el selector de
       oferta nueva ya abierto. */
    { key: 'offer', icon: IconShoppingCartPlus, label: t('onboard.offer'), description: t('onboard.offerBody'), done: catalog.length > 0, cta: t('onboard.goOffer'), onAction: () => (onCreateOffer ?? (() => onNavigate('payments')))() },
    { key: 'publish', icon: IconRocket, label: t('onboard.publish'), description: t('onboard.publishBody'), done: isPublished, cta: t('onboard.goEditor'), onAction: () => (onOpenEditorToPublish ?? onOpenEditor)() },
  ]
  /**
   * Lo pendiente va primero, en el extremo izquierdo; lo completado se conserva
   * después como historial. Dentro de cada grupo se respeta el recorrido natural
   * Páginas → Contenido → Publicar para que dos tareas con el mismo estado nunca
   * intercambien posiciones entre sí.
   */
  const orderedSetupSteps = steps
    .filter((step) => dismissedSetupSteps[step.key] === undefined)
    .sort((left, right) => Number(left.done) - Number(right.done))
  const visibleSetupSteps = orderedSetupSteps
  /**
   * Sólo espera al PROYECTO, no a las analíticas.
   *
   * Los tres pasos salen de las páginas, las colecciones y si está publicado
   * —todo del proyecto—, así que evaluarlos antes de que llegue daría 0 de 3 y
   * un parpadeo. Pero esperaba también a `loading`, que es la consulta de
   * analíticas: una petición aparte, más lenta y que este bloque no usa para
   * nada. Por eso el hueco tardaba en resolverse más que el resto del panel, y
   * era de lo último en irse aunque no tuviera nada que enseñar.
   *
   * Lo que sí espera es al catálogo: el paso de la oferta sale de él, no del
   * proyecto. Sin token no se pide, y entonces no hay nada que esperar.
   */
  const setupStepsLoading = projectLoading || (Boolean(projectId && token) && !catalogSettled)
  /* Lo pendiente que SE VE, no lo pendiente a secas: si lo único que falta se
     cerró con la ✕, quedaban sólo los hechos —que no se dibujan— y salía una
     caja vacía con «3 de 3». También decide el hueco de carga de la próxima
     vez, que si no aparecería para irse. */
  const hasPendingSteps = visibleSetupSteps.some((step) => !step.done)
  const showSetupCards = !setupStepsLoading && hasPendingSteps
  /* Mientras se sabe, el bloque guarda su sitio con tarjetas en hueco.
     Sin esto la sección nacía de altura cero y empujaba el dashboard entero
     hacia abajo al llegar los datos: lo que se veía era el panel creciendo de
     golpe, no la lista apareciendo. Quien ya no tiene pasos pendientes cambia
     un salto por otro, pero ése ocurre una vez y hacia arriba —al revés que el
     de ahora, que le pasa a todo el mundo cada vez que abre.

     Y sólo si la última vez quedaba algo pendiente: ver `readSetupHadPending`.
     Sin esa condición, quien ya terminó los tres pasos veía el esqueleto en
     cada carga para no enseñar nada después. */
  const reserveSetupRow = setupStepsLoading && visibleSetupSteps.length > 0 && setupHadPending

  /* Se anota en cuanto se sabe, para la próxima vez. Va en un efecto porque
     escribe fuera de React y no puede hacerse mientras se pinta. */
  useEffect(() => {
    if (setupStepsLoading) return
    writeSetupHadPending(projectId, hasPendingSteps)
    setSetupHadPending(hasPendingSteps)
  }, [setupStepsLoading, hasPendingSteps, projectId])

  const dashboardWidgets: DashboardWidgetSpec[] = [
    {
      id: 'project-status',
      label: t('home.widget.project'),
      defaultSpan: 4,
      content: <div className="dash-overview-project-widget" style={widgetPanel}>
        <div className="dash-overview-project-identity">
          {showHeaderSkeleton ? (
            <span className="sk" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} aria-hidden="true" />
          ) : (
            <span className={`dash-widget-glass-icon dash-widget-glass-icon--project${isPublished ? ' is-published' : ''}`}>
              {isPublished ? <IconRocket size={18} /> : <IconCloudOff size={18} />}
            </span>
          )}
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: showHeaderSkeleton ? 4 : 0 }}>
            {showHeaderSkeleton ? (
              <>
                <span className="sk" style={{ width: 150, height: 14, borderRadius: 6 }} aria-hidden="true" />
                <span className="sk" style={{ width: 210, height: 12, borderRadius: 6 }} aria-hidden="true" />
              </>
            ) : (
              <>
                {/* El estado va junto al nombre: es una propiedad del
                    proyecto, no de su dirección. En la fila de la URL competía
                    por el ancho justo con lo que más lo necesita. */}
                <div className="dash-overview-project-title">
                  <span className="dash-card-title">{projectName || t('overview.projectFallback')}</span>
                  {isPublished ? (
                    <span
                      className="dash-published-state dash-meta-icon"
                      data-tooltip={t('overview.livesite')}
                      role="img"
                      aria-label={t('overview.site.aria')}
                      tabIndex={0}
                    >
                      <IconWorld size={13} aria-hidden="true" />
                    </span>
                  ) : null}
                </div>
                <div className="dash-card-meta dash-published-url-row" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  {isPublished && siteUrl ? (
                    <span className="dash-published-link-group">
                      <a className="dash-url-link" href={siteUrl} target="_blank" rel="noreferrer">
                        {siteUrl.replace(/^https?:\/\//, '')}
                      </a>
                      <CopyLinkButton value={siteUrl} />
                    </span>
                  ) : (
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t('overview.site.unpublished')}
                    </span>
                  )}
                </div>
                {/* Icono en lugar de rótulo. "Última publicación" y
                    "Actualizado" ocupaban media línea para decir algo que un
                    cohete y un lápiz dicen igual de bien y sin robar sitio a la
                    fecha, que es el dato. El texto sigue existiendo para quien
                    no ve los iconos: va en `title` y en `aria-label`. */}
                {publishedRelative || updatedRelative ? (
                  <div className="dash-project-recency dash-card-meta">
                    {publishedRelative ? (
                      <time
                        className="dash-meta-icon"
                        dateTime={publishedAt ?? undefined}
                        data-tooltip={t('overview.tipPublished')}
                        aria-label={t('overview.lastPublished', { when: publishedRelative })}
                        tabIndex={0}
                      >
                        <IconRocket size={12} aria-hidden="true" />
                        {publishedRelative}
                      </time>
                    ) : null}
                    {publishedRelative && updatedRelative ? <span aria-hidden="true">·</span> : null}
                    {updatedRelative ? (
                      <time
                        className="dash-meta-icon"
                        dateTime={updatedAt}
                        data-tooltip={t('overview.tipEdited')}
                        aria-label={t('overview.lastUpdated', { when: updatedRelative })}
                        tabIndex={0}
                      >
                        <IconPencil size={12} aria-hidden="true" />
                        {updatedRelative}
                      </time>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
        {/* Entre el nombre y los botones apilada, y a la izquierda de todo
            cuando hay ancho. Sólo aparece con captura: un marco gris no dice
            nada que el nombre no diga ya. */}
        {homePage?.thumbnailUrl ? (
          <a
            className="dash-overview-project-preview"
            href={siteUrl ?? undefined}
            target="_blank"
            rel="noreferrer"
            aria-label={t('overview.site.openHomeAria')}
          >
            <img src={homePage.thumbnailUrl} alt="" loading="lazy" />
          </a>
        ) : null}

        <div className="dash-overview-project-actions">
          {siteUrl ? (
            <a href={siteUrl} target="_blank" rel="noreferrer" className="dash-secondary-btn dash-overview-site-btn">
              <IconExternalLink size={14} /> {t('overview.site.view')}
            </a>
          ) : null}
          <button type="button" className="dash-primary-btn dash-new-project-btn" onClick={() => onOpenEditor()}>
            <IconLayout size={14} /> {t('overview.site.openEditor')}
          </button>
        </div>
      </div>,
    },
  ]

  dashboardWidgets.push(
    {
      id: 'pages',
      label: t('home.widget.pages'),
      defaultSpan: 2,
      content: <SummaryCard
            icon={IconFileText}
            kind="pages"
            title={t('overview.card.pages')}
            count={sitePages.length}
            unit={t(sitePages.length === 1 ? 'time.page' : 'time.pages')}
            rows={pageRows}
            action={t('overview.seeMore')}
            onAction={() => onNavigate('pages')}
          />,
    },
    {
      /* El catálogo va con Páginas y CMS y no con Cobros: es contenido del
         sitio, lo ve cualquiera que pueda abrir el proyecto, y quien entra a
         Inicio a ver cuánto vendió necesita antes saber QUÉ vende y en qué
         estado está cada cosa. */
      id: 'offers',
      label: t('home.widget.offers'),
      defaultSpan: 2,
      content: <SummaryCard
            icon={IconShoppingCart}
            kind="offers"
            title={t('overview.card.offers')}
            count={catalog.length}
            unit={t(catalog.length === 1 ? 'overview.unit.offer' : 'overview.unit.offers')}
            rows={offerRows}
            action={t('overview.seeAll')}
            emptyActionLabel={t('overview.createNew')}
            onAction={() => onNavigate('payments')}
          />,
    },
    {
      id: 'cms',
      label: t('home.widget.cms'),
      defaultSpan: 2,
      appearsWhen: collections.length > 0,
      content: <SummaryCard
            icon={IconDatabase}
            kind="cms"
            title={t('overview.card.cms')}
            count={collections.length}
            unit={t(collections.length === 1 ? 'cms.collection' : 'cms.collections')}
            rows={collectionRows.length ? collectionRows : []}
            action={t('overview.seeMore')}
            emptyActionLabel={t('overview.createNew')}
            onAction={() => onNavigate('cms')}
          />,
    },
    {
      /* El blog, con sus últimas entradas. Sale con la primera. */
      id: 'blog',
      label: t('nav.blog'),
      defaultSpan: 2,
      appearsWhen: (blog?.posts.length ?? 0) > 0,
      content: <SummaryCard
            icon={IconArticle}
            kind="blog"
            title={t('nav.blog')}
            count={blog?.posts.length ?? 0}
            unit={t((blog?.posts.length ?? 0) === 1 ? 'overview.unit.post' : 'overview.unit.posts')}
            rows={blogRows}
            action={t('overview.seeMore')}
            emptyActionLabel={t('overview.createNew')}
            onAction={() => onNavigate('blog')}
          />,
    },
  )

  /* Cada widget de trabajo sale si el rol puede abrir su sección: el mismo
     criterio que el menú. Estaba todo detrás de `isAdmin` —personal de
     véltica—, de cuando estas secciones eran sólo internas. */
  {
    if (canBookings) dashboardWidgets.push(
      {
        id: 'bookings',
        label: t('section.bookings'),
        defaultSpan: 2,
        /* Con la primera oferta de reservas, aunque nadie haya reservado aún:
           ya hay algo que esperar en la agenda. */
        appearsWhen: upcomingCount > 0 || catalog.some((offer) => kindOf(offer) === 'booking'),
        content: <SummaryCard
              icon={IconCalendarEvent}
              kind="bookings"
              title={t('overview.card.bookings')}
              count={upcomingCount}
              unit={t(upcomingCount === 1 ? 'overview.unit.booking' : 'overview.unit.bookings')}
              rows={bookingRows}
              action={t('overview.seeAll')}
              onAction={() => onNavigate('bookingsList')}
              /* Sin citas, crear una: al calendario con el alta abierta. */
              emptyActionLabel={onCreateBooking ? t('overview.createNew') : undefined}
              onEmptyAction={onCreateBooking}
            />,
      },
    )
    if (canContacts) dashboardWidgets.push(
      {
        id: 'contacts',
        label: t('overview.card.contacts'),
        defaultSpan: 2,
        appearsWhen: contactsTotal > 0,
        content: <SummaryCard
              icon={IconAddressBook}
              kind="contacts"
              title={t('overview.card.contacts')}
              count={contactsTotal}
              unit={t(contactsTotal === 1 ? 'overview.unit.contact' : 'overview.unit.contacts')}
              rows={contactRows}
              action={t('overview.seeMore')}
              onAction={() => onNavigate('contacts')}
            />,
      },
    )
    if (canForms) dashboardWidgets.push(
      {
        id: 'forms',
        label: t('section.forms'),
        defaultSpan: 2,
        appearsWhen: submissions.length > 0,
        content: <SummaryCard
              icon={IconClipboardList}
              kind="messages"
              title={t('overview.card.forms')}
              count={submissions.length}
              unit={t(submissions.length === 1 ? 'overview.unit.message' : 'overview.unit.messages')}
              rows={submissionRows}
              action={t('overview.seeMore')}
              onAction={() => onNavigate('forms')}
            />,
      },
    )
    if (canCommerce) dashboardWidgets.push(
      {
        id: 'orders',
        label: t('overview.card.transactions'),
        defaultSpan: 2,
        appearsWhen: displayOrders.length > 0,
        content: (
            <SummaryCard
              icon={IconShoppingCart}
              kind="transactions"
              title={t('overview.card.transactions')}
              count={displayOrders.length}
              unit={t(displayOrders.length === 1 ? 'overview.unit.sale' : 'overview.unit.sales')}
              rows={orderRows}
              action={t('overview.seeMore')}
              onAction={() => onNavigate('orders')}
            />
        ),
      },
    )

    if (canCommerce) dashboardWidgets.push(
      {
        id: 'toShip',
        label: t('overview.card.toShip'),
        defaultSpan: 2,
        appearsWhen: toShipOrders.length > 0,
        content: (
            <SummaryCard
              icon={IconTruckDelivery}
              kind="transactions"
              title={t('overview.card.toShip')}
              count={toShipOrders.length}
              unit={t(toShipOrders.length === 1 ? 'overview.unit.order' : 'overview.unit.orders')}
              rows={toShipRows}
              action={t('overview.seeAll')}
              onAction={() => onNavigate('orders')}
            />
        ),
      },
    )

    /*
      Depósitos, al lado de Transacciones: una dice cuánto se vendió y la otra
      cuándo llega, que es la pregunta que hasta ahora obligaba a salir a Stripe.

      Sólo si hay cuenta conectada. Sin ella `balance` se queda en `null` —el
      servidor responde 409— y la tarjeta no se pinta: un «$0.00» en un panel sin
      Stripe no informa de nada, sólo ocupa una casilla de la rejilla.
    */
    if (canCommerce && balance) {
      dashboardWidgets.push({
        id: 'payouts',
        label: t('overview.payouts'),
        defaultSpan: 2,
        appearsWhen: displayOrders.length > 0,
        content: (
          <div className="dash-overview-summary">
            <div className="dash-overview-summary-head">
              <div className="dash-overview-summary-head-main">
                <span className="dash-widget-heading-icon" aria-hidden="true">
                  <IconBuildingBank size={18} />
                </span>
                <span className="dash-summary-head-copy">
                  <span className="dash-summary-title-row">
                    <h3 className="dash-card-title">{t('overview.payouts.title')}</h3>
                  </span>
                  <span className="dash-card-meta">{t('overview.payouts.meta')}</span>
                </span>
              </div>
              <button type="button" onClick={() => onNavigate('payouts')} className="dash-secondary-btn dash-summary-action" style={{ flexShrink: 0 }}>
                {t('overview.viewDetail')} <IconArrowUpRight size={13} />
              </button>
            </div>
            {/* Las mismas tarjetas de estadística que «Resumen»: rótulo
                arriba, icono de cristal a la derecha, cifra grande abajo. Es el
                `StatCard` de Vistas y Visitantes, no una copia — así el tamaño
                del número, el relleno y el borde no pueden separarse de los de
                al lado cuando alguien retoque aquéllos.

                Se le añadió un pie porque el dinero no se compara con el mes
                pasado: de un saldo hace falta saber cuándo llega o de dónde
                sale, y eso no cabe en la flecha con porcentaje que llevan las
                de tráfico. */}
            <div className="dash-summary-card-panel dash-payout-panel">
              <StatCard
                label={t(balance.nextPayout ? 'overview.nextPayout' : 'overview.yourBalance')}
                icon={IconBuildingBank}
                /* Sin depósito creado, la cifra es el saldo ENTERO —disponible
                   más en camino—, que es lo que Stripe llama «saldo total» en su
                   propia pantalla. Enseñar sólo el disponible era lo que hacía
                   que las dos se contradijeran: una venta recién cobrada está
                   entera en «en camino». */
                value={formatMoney(balance.nextPayout ? balance.nextPayout.amount : balance.available + balance.pending, balance.currency)}
                foot={
                  !balance.payoutsEnabled
                    ? t('overview.stripeNotEnabled')
                    : balance.nextPayout
                      /* En camino significa que Stripe ya lo mandó; pendiente,
                         que lo mandará. Para quien espera el dinero no es el
                         mismo estado, y la fecha sola no los distingue. */
                      ? t(balance.nextPayout.status === 'in_transit' ? 'overview.payout.arrivesInTransit' : 'overview.payout.arrives', { when: payoutArrival(balance.nextPayout.arrivalDate, t) })
                      : balance.available + balance.pending <= 0
                        ? t('overview.payout.none')
                        : balance.estimatedArrival
                          /* «Aprox.» porque lo es: la fecha sale del calendario
                             de la cuenta y no lleva los festivos ni las ventanas
                             bancarias que sí conoce Stripe. */
                          ? t('overview.payout.arrivesApprox', { when: payoutArrival(balance.estimatedArrival, t) })
                          : t('overview.perStripeSchedule')
                }
              />
              <StatCard
                label={t('overview.payouts.available')}
                icon={IconCash}
                value={formatMoney(balance.available, balance.currency)}
                /* Un disponible en negativo no es un error de cuentas: son
                   reembolsos y disputas ya cobrados que se comen parte de lo que
                   viene. Dicho aquí, pegado al número que extraña. */
                foot={t(balance.available < 0 ? 'overview.deductedNext' : 'overview.readyToPayout')}
              />
              <StatCard
                label={t('overview.payouts.onTheWay')}
                icon={IconClock}
                value={formatMoney(balance.pending, balance.currency)}
                /* «En camino» y no «pendiente»: es dinero ya cobrado que Stripe
                   retiene su plazo, no un cobro sin confirmar. */
                foot={t('overview.payout.withinStripe')}
              />
            </div>
          </div>
        ),
      })
    }
  }

  dashboardWidgets.push(
    {
      id: 'plan-usage',
      label: t('home.widget.usage'),
      defaultSpan: 4,
      // Guardado siempre: se consulta de vez en cuando, no cada mañana.
      defaultHidden: true,
      content: (
        <div className="dash-overview-summary">
          <div className="dash-overview-summary-head">
            <div className="dash-overview-summary-head-main">
              <span className="dash-widget-heading-icon" aria-hidden="true">
                <IconGauge size={18} />
              </span>
              <h3 className="dash-card-title">{t('overview.usage.title')}</h3>
              <span className={`dash-plan-tier-badge is-${usage?.plan ?? plan}`}>
                {(usage?.plan ?? plan) === 'free' ? 'Free' : (usage?.plan ?? plan) === 'pro' ? 'Pro' : 'Business'}
              </span>
            </div>
            <button type="button" onClick={() => onNavigate('usage')} className="dash-secondary-btn dash-summary-action" style={{ flexShrink: 0 }}>
              {t('overview.viewDetail')} <IconArrowUpRight size={13} />
            </button>
          </div>
          <div className="dash-summary-card-panel dash-usage-list-panel">
            {/* Lo del proyecto primero, que es lo que se toca a diario, y lo
                que se reinicia por periodo al final con su fecha: un contador
                al 90% preocupa distinto si mañana vuelve a cero. */}
            <div className="dash-usage-grid">
            <UsageBar
              label={t('overview.usage.pages')}
              icon={IconFileText}
              resource="pages"
              plan={plan}
              used={sitePages.length}
              limit={SITE_PLAN_LIMITS[plan].pages}
              format={(value) => formatNumber(value)}
            />
            <UsageBar
              label={t('overview.usage.collections')}
              icon={IconDatabase}
              resource="collections"
              plan={plan}
              used={collections.length}
              limit={SITE_PLAN_LIMITS[plan].collections}
              format={(value) => formatNumber(value)}
            />
            <UsageBar
              label={t('overview.usage.cmsItems')}
              icon={IconFiles}
              resource="cmsItems"
              plan={plan}
              used={collections.reduce((total, collection) => total + (collection.items?.length ?? 0), 0)}
              limit={SITE_PLAN_LIMITS[plan].cmsItems}
              format={(value) => formatNumber(value)}
            />
            {/* El catálogo entero: un producto digital, un servicio y uno con
                agenda gastan el mismo hueco, así que es UNA barra y no tres.
                Sólo cuando el número ha llegado. */}
            {publishedOffers === null ? null : (
              <UsageBar
                label={t('overview.usage.offers')}
                icon={IconShoppingCart}
                resource="offers"
                plan={plan}
                used={publishedOffers}
                limit={SITE_PLAN_LIMITS[plan].offers}
                format={(value) => formatNumber(value)}
              />
            )}
            {/* Sólo con permiso: sin él la lista de contactos ni se pide, y un
                0/100 aquí sería un dato inventado. */}
            {canContacts ? (
              <UsageBar
                label={t('overview.usage.contacts')}
                icon={IconAddressBook}
                resource="contacts"
                plan={plan}
                used={contactsTotal}
                limit={SITE_PLAN_LIMITS[plan].contacts}
                format={(value) => formatNumber(value)}
              />
            ) : null}
            {usage ? (
              <>
                <UsageBar
                  label={t('overview.usage.ai')}
                  icon={MascotIcon}
                  resource="ai"
                  plan={plan}
                  used={usage.used.aiCredits}
                  limit={usage.limits.ai_credits}
                  resetAt={usage.aiCreditsResetAt ?? undefined}
                  format={(value) => formatNumber(value)}
                />
                <UsageBar
                  label={t('overview.usage.forms')}
                  icon={IconClipboardList}
                  resource="forms"
                  plan={plan}
                  used={usage.used.formSubmissions}
                  limit={usage.limits.form_submissions}
                  resetAt={usage.formSubmissionsResetAt}
                  format={(value) => formatNumber(value)}
                />
              </>
            ) : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'domain',
      label: t('home.widget.domain'),
      defaultSpan: 2,
      defaultHidden: true,
      content: (
        <div className="dash-overview-summary">
          <div className="dash-overview-summary-head">
            <div className="dash-overview-summary-head-main">
              <span className="dash-widget-heading-icon" aria-hidden="true">
                <IconWorld size={18} />
              </span>
              <h3 className="dash-card-title">{t('overview.domain.title')}</h3>
            </div>
            <button type="button" onClick={() => onNavigate('publish')} className="dash-secondary-btn dash-summary-action" style={{ flexShrink: 0 }}>
              {t('overview.manage')} <IconArrowUpRight size={13} />
            </button>
          </div>
          <div className="dash-summary-card-panel dash-domain-overview-panel">
            {domains.length === 0 ? (
              <div className="dash-widget-empty">
                <span>{t('overview.domain.none')}</span>
                <button
                  type="button"
                  onClick={() => onNavigate('publish')}
                  className="dash-widget-empty-link"
                >
                  {t('overview.domain.getDomain')}
                </button>
              </div>
            ) : domains.map((domain) => (
              /* Estado del dominio y del certificado por separado: un dominio
                 puede resolver y seguir sin SSL, y ahí es donde la gente se
                 queda mirando un aviso del navegador sin saber por qué. */
              <div className="dash-domain-row" key={domain.id}>
                <span className="dash-widget-glass-icon dash-stat-glass-icon dash-domain-row-icon" aria-hidden="true">
                  <IconShieldCheck size={14} />
                </span>
                <span className="dash-domain-name">
                  {domain.hostname}
                  {domain.isPrimary ? <em>{t('overview.domain.primary')}</em> : null}
                </span>
                <span className={`dash-domain-chip is-${domain.status}`}>
                  {t(domain.status === 'active' ? 'overview.domain.active' : domain.status === 'error' ? 'overview.domain.error' : 'overview.domain.configuring')}
                </span>
                <span className={`dash-domain-chip ${domain.sslStatus === 'active' ? 'is-active' : 'is-verifying'}`}>
                    {/* Dos claves enteras: en inglés «SSL ready» no se compone
                        pegando una palabra detrás de la sigla. */}
                    {t(domain.sslStatus === 'active' ? 'overview.ssl.ready' : 'overview.ssl.pending')}
                </span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'notes',
      label: t('home.widget.notes'),
      defaultSpan: 4,
      /* Las notas, siempre al final: lo que va apareciendo entra antes. */
      stayLast: true,
      content: (
        <div className="dash-overview-summary">
          <div className="dash-overview-summary-head">
            <div className="dash-overview-summary-head-main">
              <span className="dash-widget-heading-icon" aria-hidden="true">
                <IconNote size={18} />
              </span>
              {/* Título y descripción apilados, como en las demás tarjetas: sin
                  la segunda línea el encabezado de Notas quedaba a otra altura
                  que el de sus vecinas y la fila no cuadraba. */}
              <span className="dash-summary-head-copy">
                <span className="dash-summary-title-row">
                  <h3 className="dash-card-title">{t('overview.notes.title')}</h3>
                </span>
                <span className="dash-card-meta">{t('overview.notes.meta')}</span>
              </span>
            </div>
          </div>
          <div className="dash-summary-card-panel dash-notes-panel">
            <ProjectNotes
              projectId={projectId}
              token={token}
              initial={projectNotes}
              currentUser={currentUser}
              canWrite={can(projectRole, 'notes.write')}
              canModerate={can(projectRole, 'team.manage')}
              loading={projectLoading}
            />
          </div>
        </div>
      ),
    },
  )

  dashboardWidgets.push({
    id: 'analytics',
    label: t('overview.analyticsSummary'),
    defaultSpan: 4,
    /* Sin plan, guardado: es la muestra borrosa tras el candado, y no la
       primera cosa que hay que ver de un proyecto recién hecho. Con plan, sale
       con las primeras visitas. */
    ...(isPaid ? { appearsWhen: hasTraffic } : { defaultHidden: true }),
    content: isPaid ? (
        displayLoading || (hasTraffic && displayStats) ? (
          <AnalyticsSummarySection stats={displayStats} loading={displayLoading} onNavigate={onNavigate} />
        ) : (
          /* Sin visitas, el widget conserva su encabezado —miniatura, título y
             enlace— y el aviso va debajo: un hueco centrado sin título no decía
             de qué era. */
          <AnalyticsSummarySection
            stats={null}
            loading={false}
            onNavigate={onNavigate}
            empty
          />
        )
      ) : (
        <FeatureUpsell onOpenPlans={onOpenPlans}>
          <AnalyticsSummarySection stats={fake} loading={false} onNavigate={onNavigate} />
        </FeatureUpsell>
      ),
  })

  /*
   * El resumen de ventas, gemelo del de arriba.
   *
   * Sólo para administradores y sólo con plan: es la misma regla que siguen
   * Transacciones y Depósitos, y por el mismo motivo —quien no puede entrar a
   * Cobros tampoco puede ver aquí cuánto se facturó—. No lleva versión falsa
   * tras el candado como el de tráfico: sin plan no se pide la consulta, así que
   * por ese navegador no pasa ni una cifra real ni una inventada.
   *
   * `everSold === false` es un sitio que nunca ha vendido: ahí las cuatro
   * tarjetas en cero no informan de nada y el widget dice qué falta.
   *
   * Si la consulta falla el widget no se pinta, igual que el de Depósitos
   * cuando no hay cuenta conectada. La alternativa era dejarlo con el esqueleto
   * puesto para siempre, que es peor que no estar: parece que sigue cargando.
   */
  if (canCommerce && isPaid && (salesLoading || sales)) {
    dashboardWidgets.push({
      id: 'salesSummary',
      label: t('home.widget.sales'),
      defaultSpan: 4,
      appearsWhen: sales?.everSold === true,
      content: salesLoading || sales?.everSold !== false ? (
        <SalesSummarySection stats={sales} loading={salesLoading} onNavigate={onNavigate} />
      ) : (
        <SalesSummarySection
          stats={null}
          loading={false}
          onNavigate={onNavigate}
          empty
        />
      ),
    })
  }

  /* El orden de un dashboard recién estrenado: el sitio arriba, luego lo que
     se construye (páginas, catálogo, blog, citas), después el resto y las
     notas al final. Los que todavía no tienen nada esperan y, cuando aparecen,
     entran al final —antes de las notas—, nunca encima de lo que ya había
     (ver `CustomizableDashboardGrid`). Con disposición guardada manda la suya. */
  const orderedDashboardWidgets = [...dashboardWidgets].sort((a, b) => dashboardRank(a.id) - dashboardRank(b.id))

  return (
    <div className={`dash-overview-view dash-granular-loading${revealAnalytics ? ' dash-reveal-loaded' : ''}`} style={{ display: 'flex', flexDirection: 'column', gap: 28, paddingBottom: 64 }}>
      {/* La misma mascota que recibe en la pantalla sin proyectos, y por lo
          mismo: el saludo ya dice la hora y el nombre, pero lo dice un texto.
          Con ella hay alguien saludando, y es la única señal de que esta
          pantalla es la casa y no una sección más del panel.

          Alineada a la izquierda, sobre el saludo: centrada partiría en dos un
          encabezado que tiene sus acciones a la derecha.

          Y se pulsa: abre los colores del panel. Ver `MascotColorPicker`. */}
      {/* Antes del saludo: una invitación esperando respuesta es lo único de
          esta pantalla que le PIDE algo a quien la mira, y pesa más que la frase
          del día. Se pinta sola cuando no hay ninguna. */}
      <TeamInviteBanner
        token={token}
        onAccepted={(id, name, role) => onEnterAcceptedProject?.(id, name, role)}
      />
      {/* Y el traspaso, por la misma razón que los dos de arriba y con más
          motivo: quien recibe un sitio casi siempre YA tiene el suyo abierto, así
          que la pantalla de arranque —donde estaba sólo este aviso— no la ve
          nunca. El correo puede caer en spam, y sin esto el traspaso se queda
          esperando una respuesta que nadie sabe que tiene que dar. */}
      <TransferBanner token={token} />
      {/* Los proyectos borrados NO se avisan aquí, y es a propósito.
          Su sitio es el selector del menú de la izquierda, donde salen apagados
          con su plazo: ahí es donde se buscan los proyectos, y ahí no molestan.
          Como banda eran un aviso por cada uno —siete borrados, siete recuadros
          idénticos empujando el panel hacia abajo— para algo que no urge en
          treinta días. En la pantalla sin proyectos sí van, porque allí no hay
          menú lateral y serían inalcanzables. Ver `DeletedProjectsBanner`. */}
      {/* Y el repaso de lo que acaba de llegar, si este proyecto se recibió de
          otra cuenta. Vive aquí y no en una pantalla propia tras aceptar porque
          dos de sus tres acciones salen de la aplicación —Stripe y el pago del
          plan— y vuelven por una redirección: una pantalla intermedia se
          perdería en ese viaje, el inicio del proyecto no. */}
      <ProjectHandover
        token={token}
        projectId={projectId}
        onOpenSection={(section) => onNavigate(section)}
        onOpenPlans={onOpenPlans}
      />

      <header className={`dash-overview-hello${chatting ? ' is-chatting' : ''}`}>
        <MascotColorPicker gaze={waiting ? 'up' : undefined} />
        {/* Con el nombre todavía sin llegar se saluda como siempre: «Actualmente
            estás en «»» es peor que no decir nada, y el nombre puede tardar un
            instante en llegar del servidor la primera vez que se abre un
            proyecto. */}
        <div className="dash-home-conversation">
          <MascotGreeting
            /* Con algo esperando, el saludo deja de ser un saludo.
               «Buenas tardes, Heriberto» encima de un aviso sin responder es la
               frase de alguien que no se ha enterado de nada: la mascota está
               mirando hacia arriba y lo que dice tiene que ir a juego. El saludo
               normal vuelve solo en cuanto no queda nada pendiente. */
            /* Editando, tampoco saluda: «Buenas tardes, Heriberto» encima de una
               rejilla que se está reacomodando es la frase de alguien que no se
               ha enterado de lo que estás haciendo — el mismo motivo por el que
               ya no saluda cuando hay algo esperando respuesta. El saludo de la
               hora vuelve solo al pulsar «Listo». */
            greeting={widgetsEditing
              ? t('overview.editingWidgetsGreeting')
              : waitingGreeting
                ?? (arrivedBySwitch && projectName ? switchGreeting(projectName) : userGreeting(userName))}
            facts={mascotFacts}
            ready={!projectLoading}
            /* Lo pendiente manda sobre la llegada: haber cambiado de proyecto es
               una noticia de hace un segundo, y algo esperando respuesta es una
               deuda. Si hay las dos, se dice la que le cuesta algo a quien mira. */
            /* Editando, la mascota habla de lo que estás haciendo ahora mismo y
               manda sobre todo lo demás: un aviso pendiente o un «cambiaste de
               sitio» son de antes, y esto es de este segundo. Vuelve solo al
               salir del modo. */
            pinned={widgetsEditing
              ? { text: t('overview.editingWidgets') }
              : waitingLine ?? (arrivedBySwitch && projectName ? switchLine() : undefined)}
          />
        </div>
      </header>
      {/* La caja de la IA, centrada bajo el saludo y a lo ancho de una
          conversación: es lo primero que se puede HACER en esta pantalla.
          Editando la rejilla no, que el globo habla de eso. */}
      {widgetsEditing ? null : <HomeAssistant userName={currentUser?.name || userName} avatarUrl={currentUser?.avatarUrl} />}
      {reserveSetupRow ? (
        <section className="dash-setup-inbox dash-setup-inbox--skeleton" aria-hidden="true">
          <header className="dash-setup-inbox-head">
            <div>
              <span className="sk" style={{ width: 74, height: 10, borderRadius: 5 }} />
              <span className="sk" style={{ width: 190, height: 15, borderRadius: 6 }} />
            </div>
            <span className="sk" style={{ width: 88, height: 10, borderRadius: 5 }} />
          </header>
          <div className="dash-setup-grid">
          {orderedSetupSteps.map((step) => (
            /* Reusa las clases de la fila de verdad en vez de dibujar una caja
               aparte: así el hueco mide EXACTAMENTE lo que va a medir la
               bandeja. Con una versión simplificada salía más corto y el salto
               seguía ahí, sólo que más tarde. */
            <article className="dash-setup-note" key={step.key}>
              <div className="dash-setup-note-top"><span className="sk dash-setup-tile-skeleton" /></div>
              <div className="dash-setup-note-copy">
                <span className="sk" style={{ width: '72%', height: 13, borderRadius: 6 }} />
                <span className="sk" style={{ width: '92%', height: 11, borderRadius: 6 }} />
              </div>
              <span className="sk" style={{ width: 96, height: 30, borderRadius: 9 }} />
            </article>
          ))}
          </div>
        </section>
      ) : null}
      {showSetupCards ? (
        <SetupStepsRow
          steps={orderedSetupSteps}
          onDismiss={(key) => {
            setDismissedSetupSteps((current) => {
              const next = { ...current, [key]: Date.now() }
              writeSetupStepDismissals(projectId, next)
              return next
            })
          }}
        />
      ) : null}
      <div className="dash-overview-widgets-bar">
        {/* Encima de la rejilla, que es lo que edita; arriba competía con la
            caja de la IA. Editando, «Listo» se va con «Agregar» a la barra de
            arriba —el mismo hueco, para que los dos mandos de la edición estén
            juntos—. */}
        {widgetsEditing && widgetToolbarSlot
          ? createPortal(widgetEditToggle, widgetToolbarSlot)
          : widgetEditToggle}
      </div>
      <CustomizableDashboardGrid key={projectId} storageKey={`veltica:dashboard-layout:v2:${projectId}`} widgets={orderedDashboardWidgets} editing={widgetsEditing} onEdit={() => setWidgetsEditing(true)} />
      {/* La misma ficha que en Cobros: se consulta una venta y se cierra, sin
          salir del inicio ni perder la disposición de los widgets. */}
      {/* La MISMA ficha de clase que la lista de Citas, no una parecida: enseña
          el desglose de estados y deja cambiarlos, que es la mitad de para lo
          que se abre un grupo. Escribir aquí otra habría sido dos pantallas del
          mismo dato divergiendo a la primera corrección. */}
      {openGroup ? (
        <GroupBookingDialog
          entry={openGroup}
          {...(() => {
            const shot = openGroup.lead.offerId ? serviceInfo.get(openGroup.lead.offerId)?.image : undefined
            return shot ? { image: shot } : {}
          })()}
          busyId={null}
          onClose={() => setOpenGroup(null)}
          onOpen={(bookingId) => {
            /* La clase NO se cierra: se queda detrás. Cerrarla al abrir a una
               de sus personas borraba el sitio del que se venía, y volver al
               grupo exigía buscarlo otra vez en la lista. */
            const member = openGroup.members.find((row) => row.id === bookingId)
            if (member) setOpenBooking(member)
          }}
          onStatusChange={async (booking, status) => {
            const updated = await updateBooking(projectId, booking.id, { status }, token)
            setUpcoming((current) => current.map((row) => (row.id === updated.id ? updated : row)))
            setOpenGroup((current) => (current
              ? { ...current, members: current.members.map((row) => (row.id === updated.id ? updated : row)) }
              : current))
          }}
        />
      ) : null}

      {/* La misma ficha que en Citas, sin salir del inicio. */}
      {openBooking ? (
        <BookingDetail
          projectId={projectId}
          token={token}
          booking={openBooking}
          {...(onOpenService ? { onOpenService } : {})}
          {...(() => {
            const info = openBooking.offerId ? serviceInfo.get(openBooking.offerId) : undefined
            return {
              ...(info?.image ? { image: info.image } : {}),
              ...(info ? { capacity: info.capacity } : {}),
            }
          })()}
          /* Sin esto el botón «Ver contacto» no se pinta: la ficha lo esconde
             cuando no hay a dónde llevar. Es el mismo camino que usan las filas
             de Contactos de este mismo inicio. */
          {...(onOpenContact ? { onOpenContact } : {})}
          onChange={(updated) => {
            setOpenBooking(updated)
            /* Y la lista de debajo se entera: cambiar el estado desde la ficha
               y ver la fila vieja detrás es lo que hace dudar de si se guardó. */
            setUpcoming((current) => current.map((row) => (row.id === updated.id ? updated : row)))
          }}
          onClose={() => setOpenBooking(null)}
        />
      ) : null}

      {openOrder ? (
        <OrderDetailDialog
          projectId={projectId}
          token={token}
          order={openOrder}
          // El inicio no lleva la cuenta del registro abierto —eso es del taller—,
          // así que aquí se llega a Ventas y la oferta se abre desde su lista.
          onOpenOffer={() => { setOpenOrder(null); onNavigate('payments') }}
          onClose={() => setOpenOrder(null)}
        />
      ) : null}
    </div>
  )
}
